/* =========================================================================
   Press Comparison Quiz Engine — createElement refactor
   Liquid renders products as hidden divs, JS reads dataset via dataset.
   Zero API calls, zero external product fetches.

   Architecture: PC_STATE + PC_STEPS data model with createElement helpers.
   Visuals: v3 patterns (start screen, pill progress, opt-grid, gradient btns).

   Key changes from prior version:
     - InnerHTML template strings → createElement-based DOM building
     - Flat PC object → structured PC_STATE / PC_STEPS / PC_DOM pattern
     - renderStep(innerHTML) → renderStep(buildStepCard + appendChildren)
     - Score engine factored into clear, testable functions
     - Consistent error boundaries (early-return guards on every public function)
    ========================================================================= */
(function() {
  'use strict';

/* =========================================================================
   ELEMENT HELPER — single source of truth for DOM creation
   Pattern from reference: eliminate innerHTML template string noise.
    ========================================================================= */
  function createElement(tag, props) {
    var el = document.createElement(tag);
    if (!props) return el;
    if (props.className) el.className = props.className;
    if (props.id) el.id = props.id;
    if (props.style) el.setAttribute('style', props.style);
    if (props.textContent !== undefined) el.textContent = props.textContent;
    if (props.innerHTML !== undefined) el.innerHTML = props.innerHTML;
    if (props.href) el.href = props.href;
    if (props.target) el.target = props.target;
    if (props.rel) el.rel = props.rel;
    if (props.src) el.src = props.src;
    if (props.alt !== undefined) el.setAttribute('alt', props.alt);
    if (props.loading) el.loading = props.loading;
    if (props.disabled !== undefined) el.disabled = props.disabled;
    if (props.type !== undefined) el.type = props.type;
    if (props.name !== undefined) el.name = props.name;
    if (props.role) el.setAttribute('role', props.role);
    if (props.tabindex) el.setAttribute('tabindex', props.tabindex);
    if (props.autofocus) el.autofocus = props.autofocus;
    if (props['aria-selected'] !== undefined) el.setAttribute('aria-selected', props['aria-selected']);
        /* Catch-all: any property not explicitly handled goes to setAttribute */
      for (var key in props) {
        if (props.hasOwnProperty(key) && el[key] === undefined)
          el.setAttribute(key, props[key]);
        }
    return el;
  }

  /* Append multiple children in one call */
  function appendChildren(parent, children) {
    for (var i = 0; i < children.length; i++) {
      if (children[i]) parent.appendChild(children[i]);
    }
    return parent;
  }

/* =========================================================================
   STATE — flat, resettable object. Every field documented.
    ========================================================================= */
  var PC_STATE = {
    products: [],
    step: 0,
    answers: {},
    scored: null,
    top3: null,
    profile: {
      apparel: 0, caps: 0, mugs: 0, 'sports-balls': 0, 'large-format': 0, 'high-volume': 0,
      low: 0, medium: 0, high: 0, industrial: 0,
      entry: 0, mid: 0, pro: 0, premium: 0, enterprise: 0,
      manual: 0, 'auto-open': 0, pneumatic: 0, 'rotary-auto': 0,
      'large-platen': 0, 'dual-station': 0, 'roll-to-roll': 0, compact: 0, beginner: 0, 'high-speed': 0
    }
  };

/* =========================================================================
   STEPS — immutable config array. Add/remove steps here only.
   Each step: key, weight, title, type, options with value/icon/label/sublabel.
    ========================================================================= */
  var PC_STEPS = [
    {
      key: 'useCase', weight: 3, title: 'What is your primary application?', type: 'single',
      options: [
        { value: 'apparel', icon: '&#128093;', label: 'Standard garments', sublabel: 'T-shirts, hoodies, DTF/DTG' },
        { value: 'caps', icon: '&#129522;', label: 'Caps & headwear', sublabel: 'Snapbacks, dad hats, trucker' },
        { value: 'mugs', icon: '&#9749;', label: 'Mugs & drinkware', sublabel: 'Coffee mugs, tumblers' },
        { value: 'large-format', icon: '&#127757;', label: 'Large format', sublabel: 'Flags, banners, all-over sublimation' },
        { value: 'sports-balls', icon: '&#9917;', label: 'Sports balls & specialty', sublabel: 'Basketballs, footballs, soccer' },
        { value: 'high-volume', icon: '&#127959;', label: 'High-volume DTF', sublabel: 'Multi-product shop' }
      ]
    },
    {
      key: 'volume', weight: 2, title: 'What is your production volume?', type: 'single',
      options: [
        { value: 'low', icon: '&#128104;', label: 'Under 200/mo', sublabel: 'Starter shop' },
        { value: 'medium', icon: '&#128105;', label: '200-1,000/mo', sublabel: 'Growing business' },
        { value: 'high', icon: '&#128074;', label: '1,000-5,000/mo', sublabel: 'Established production' },
        { value: 'industrial', icon: '&#127961;', label: '5,000+/mo', sublabel: 'Industrial scale' }
      ]
    },
    {
      key: 'budget', weight: 3, title: 'What is your budget range?', type: 'single',
      options: [
        { value: 'entry', icon: '&#128176;', label: 'Under $1,000', sublabel: 'Starter / hobby' },
        { value: 'mid', icon: '&#128177;', label: '$1,000 - $2,999', sublabel: 'Small business' },
        { value: 'pro', icon: '&#128178;', label: '$3,000 - $6,999', sublabel: 'Growing shop' },
        { value: 'premium', icon: '&#127942;', label: '$7,000 - $14,999', sublabel: 'High-volume' },
        { value: 'enterprise', icon: '&#128189;', label: '$15,000+', sublabel: 'Industrial automation' }
      ]
    },
    {
      key: 'automation', weight: 3, title: 'What automation level do you prefer?', type: 'single',
      options: [
        { value: 'manual', icon: '&#128079;', label: 'Fully manual', sublabel: 'Maximum control, lowest cost' },
        { value: 'auto-open', icon: '&#127919;', label: 'Auto-open / electromagnetic', sublabel: 'Easy & consistent' },
        { value: 'pneumatic', icon: '&#128160;', label: 'Pneumatic / air-powered', sublabel: 'Less effort, high consistency' },
        { value: 'rotary-auto', icon: '&#129302;', label: 'Fully automatic rotary', sublabel: 'Set & forget' }
      ]
    },
    {
      key: 'voltage', weight: 2, title: 'What voltage is available in your shop?', type: 'single',
      options: [
        { value: '120v', icon: '&#9889;', label: '120V', sublabel: 'Standard outlet' },
        { value: '240v', icon: '&#9889;', label: '240V', sublabel: 'Industrial outlet' },
        { value: '3-phase', icon: '&#9889;', label: '240V 3-Phase', sublabel: 'Heavy-duty production' }
      ]
    }
  ];

  var PC_DOM = {};
  var BUDGET_RANGES = { 
    entry: [0, 100000],      // Under $1,000
    mid: [100000, 299900],   // $1,000 - $2,999
    pro: [300000, 699900],   // $3,000 - $6,999
    premium: [700000, 1499900], // $7,000 - $14,999
    enterprise: [1500000, 99999999] // $15,000+
  };

  /* Convert raw price (in cents) to formatted dollar string with cents */
  function formatPrice(price) {
    var p = Number(price || 0);
    return (p / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

/* =========================================================================
   PUBLIC API — only these functions touch the outside world.
    ========================================================================= */
  window.PC_INIT = function() {
    resetState();
    var root = getRoot();
    if (!root) return;
    root.removeAttribute('data-loading');

    /* Persistent nav — built once, survives all view transitions */
    if (!root.querySelector('.pc-nav-header')) {
      buildNavHeader(root);
    }

    loadProducts();
    ensureContainer(root);
    buildStepBar();

    var productCount = PC_STATE.products.length;
    if (productCount > 0 && productCount <= 3) {
      renderResultsPage();
      return;
    }
    renderStartScreen();
  };

  window.PC_RESET = function() { window.PC_INIT(); };

  function resetState() {
    PC_STATE.step = 0;
    PC_STATE.answers = {};
    PC_STATE.scored = null;
    PC_STATE.top4 = null;
    PC_STATE.profile = {
      apparel: 0, caps: 0, mugs: 0, 'sports-balls': 0, 'large-format': 0, 'high-volume': 0,
      low: 0, medium: 0, high: 0, industrial: 0,
      entry: 0, mid: 0, pro: 0, premium: 0, enterprise: 0,
      manual: 0, 'auto-open': 0, pneumatic: 0, 'rotary-auto': 0,
      'large-platen': 0, 'dual-station': 0, 'roll-to-roll': 0, compact: 0, beginner: 0, 'high-speed': 0
    };
  }

/* =========================================================================
   PRODUCT LOADING — Liquid renders hidden divs, JS reads dataset.
   Data pattern: iterate .pc-product-item divs, extract data-* attrs.
   Derives useCase from title keywords + platen size heuristics.
    ========================================================================= */
  function getRoot() {
    return document.getElementById('pc-root');
  }

  function ensureContainer(root) {
    var c = root.querySelector('.pc-container');
    if (!c) {
      c = createElement('div', { className: 'pc-container' });
      root.appendChild(c);
    }
    PC_DOM.root = root;
    PC_DOM.container = c;
    return c;
  }

  function loadProducts() {
    var divs = document.querySelectorAll('.pc-product-item');
    if (!divs || divs.length === 0) return [];

    var products = [];
    for (var i = 0; i < divs.length; i++) {
      var div = divs[i];
      var get = function(attr) { return div.getAttribute(attr) || ''; };
      var num = function(attr, fb) {
        var v = parseFloat(get(attr));
        return isNaN(v) ? (fb || 0) : v;
      };

      var title = get('data-title');
      var sku = get('data-sku');
      /* Prefer Liquid's series (contains-match on full title) over JS first-word guess */
      var series = get('data-series') || deriveSeries(title);
      var uc = 'apparel';
      var platen = (get('data-platen') || '').toUpperCase();
      var pw = get('data-platen-w') || '';
      var ph = get('data-platen-h') || '';

      /* Derive primary useCase from physical characteristics */
      uc = deriveUseCase(title, platen, pw, ph);

        /* Skip non-matching product types (Automation Add-Ons, accessories, etc.) */
      if (!isHeatPress({productType: get('data-product-type')})) continue;

      products.push({
        id: num('data-product-id', 0),
        handle: get('data-handle'),
        sku: sku,
        variantId: num('data-variant-id', 0),
        title: title,
        price: num('data-price', 0),
        image: get('data-image') || get('data-src') || '',
        capacity: get('data-capacity') || 'medium',
        voltage: get('data-voltage'),
        voltageRaw: get('data-voltage-raw') || get('data-voltage'),
        watts: get('data-watts'),
        platen: get('data-platen'),
        platenW: pw,
        platenH: ph,
        platenArea: calcPlatenArea(pw, ph),
        method: get('data-method'),
        amps: get('data-amps'),
        workstationCount: get('data-workstation-count'),
        compressedAirFlowCfm: get('data-compressed-air-flow-cfm'),
        tempRange: get('data-temp'),
        weight: get('data-weight'),
        series: series,
        productType: get('data-product-type'),
        useCase: uc,
        description: get('data-description') || ''
      });
    }

    PC_STATE.products = products;
    return products;
  }

  /* SKU-based matching for high-volume DTF: X4, X6, X8, XR1515, XR1620 (incl. XR1620-2, XR1620-3) */
  var PC_HIGH_VOLUME_SKUS = ['X4', 'X6', 'X8', 'XR1515', 'XR1620', 'XR1620-2', 'XR1620-3'];
  function isHighVolumeSKU(sku) {
    if (!sku) return false;
    var s = sku.trim().toUpperCase();
    for (var i = 0; i < PC_HIGH_VOLUME_SKUS.length; i++) {
      if (s === PC_HIGH_VOLUME_SKUS[i]) return true;
    }
    return false;
  }

  function deriveSeries(title) {
    var firstWord = (title.split(' ')[0] || '').toUpperCase();
    if (firstWord === 'APEX' || firstWord === 'TITAN' || firstWord === 'ATLAS' || firstWord === 'VECTOR') {
      return firstWord;
    }
    return 'VECTOR';
  }

  /* Derive useCase from title keywords + platen size heuristics.
     Priority: mugs/balls > caps > large-format > apparel (default). */
  function deriveUseCase(title, platenUpper, pw, ph) {
    var tt = title.toUpperCase();

    /* Mugs: strict word-boundary match for MUG/TUMBLER in title */
    var mugMatch = /\bMUG(?:S|PRESS|TRANSFER|GRINDER)?\b/i.test(title);
    var tumblerMatch = /\bTUMBLER\b/i.test(title);
    if (mugMatch || tumblerMatch) return 'mugs';

    /* Sports balls */
    if (tt.indexOf('BALL') !== -1 || tt.indexOf('SPHERE') !== -1) return 'sports-balls';

    /* Caps: title or platen contains "CAP" */
    if (tt.indexOf('CAP') !== -1 || (platenUpper || '').indexOf('CAP') !== -1) return 'caps';

    /* Large format: ATLAS series by definition, or platen >= 936 sq in */
    if (tt.indexOf('ATLAS') !== -1) return 'large-format';
    var area = calcPlatenArea(pw, ph);
    if (area >= 936) return 'large-format';

    return 'apparel';
  }

  function calcPlatenArea(w, h) {
    if (!w || !h) return 0;
    var wn = parseFloat(w), hn = parseFloat(h);
    return isNaN(wn) || isNaN(hn) ? 0 : wn * hn;
  }

       /* =========================================================================
     PRODUCT TYPE WHITELIST -- only "Heat Press Machines" and
         "Mug & Tumbler Presses".  Everything else (Automation Add-Ons,
     accessories, labels, etc.) is silently excluded.
         ==================================================================*/
  function isHeatPress(product) {
    if (!product.productType) return false;
    var pt = product.productType.toUpperCase().trim();
    return pt.indexOf('HEAT PRESS') !== -1
          || pt.indexOf('MUG PRESS') !== -1
          || pt.indexOf('MUG & TUMBLER') !== -1
          || pt.indexOf('TUMBLER PRESS') !== -1
          || pt.indexOf('MUGS') !== -1
          || pt.indexOf('TUMBLER') !== -1;
   }


/* =========================================================================
   START SCREEN — v3 trust-signal pattern, built with createElement.
    ========================================================================= */
  function renderStartScreen() {
    var root = getRoot();
    if (!root) return;
    var c = PC_DOM.container || ensureContainer(root);
    var bodyEl = c.querySelector('.pc-body');
    if (!bodyEl) {
      bodyEl = createElement('div', { className: 'pc-body' });
      c.appendChild(bodyEl);
    }
    clearChildren(bodyEl);

      /* Remove pill progress bar on start screen */
    var pillBar = c.querySelector('.pc-pill-progress');
    if (pillBar) pillBar.remove();

    /* Build start screen via createElement (was innerHTML template string) */
    var trustAvatars = createElement('div', { className: 'pc-trust-avatars' });
    var badgeIcons = [
      '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 3l8 4v5c0 5-3.5 9.4-8 11-4.5-1.6-8-6-8-11V7l8-4z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M9 12l2 2 4-4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="4" y="11" width="16" height="3" rx="1" fill="white"/><path d="M6 11V8a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v3" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M8 19h8" stroke="white" stroke-width="2" stroke-linecap="round"/><path d="M10 14v5" stroke="white" stroke-width="2" stroke-linecap="round"/><path d="M14 14v5" stroke="white" stroke-width="2" stroke-linecap="round"/></svg>',
      '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2l2.9 6.1 6.7 1-4.8 4.7 1.1 6.7L12 17.8l-6 3.2 1.1-6.7L2.3 9.1l6.7-1L12 2z" fill="white"/></svg>'
    ];
    ['#3780C3', '#3FA9F5', '#10b981'].forEach(function(color, idx) {
      var avatar = createElement('span', { className: 'pc-avatar' });
      avatar.style.cssText = 'width:40px;height:40px;border-radius:999px;display:flex;align-items:center;justify-content:center;background:' + color + ';';
      avatar.innerHTML = badgeIcons[idx];
      trustAvatars.appendChild(avatar);
    });

    var trustRow = createElement('div', { className: 'pc-start-trust-row' });
    trustRow.appendChild(trustAvatars);
    /* "Trusted by 4,200+ ..." — use innerHTML only for the <strong> tag */
    trustRow.innerHTML = '';
    trustRow.appendChild(trustAvatars);
    var trustSpan = document.createElement('span');
    trustSpan.innerHTML = root.getAttribute('data-pc-social-proof') || ' Trusted by garment decorators &amp; DTF shops';
    trustRow.appendChild(trustSpan);

    var titleEl = createElement('h2', { className: 'pc-start-title' });
    titleEl.innerHTML = root.getAttribute('data-pc-heading') || 'Find the Perfect Heat<br>Press <span class="pc-grad-orange">for Your Shop</span>';

    var subtitleEl = createElement('p', {
      className: 'pc-start-subtitle',
      textContent: root.getAttribute('data-pc-subheading') || 'Intelligent quiz that analyzes your production needs and recommends the ideal machine from our collection.'
    });

    var startBtn = createElement('button', {
      className: 'pc-start-btn',
      textContent: 'Start Free Quiz ',
      autofocus: true
    });
    /* Append arrow icon span */
    var arrowIcon = document.createElement('i');
    arrowIcon.className = 'fa-solid fa-arrow-right pc-start-btn-arrow';
    startBtn.appendChild(arrowIcon);

    var featuresEl = createElement('div', { className: 'pc-start-features' });
    ['Instant personalized results', 'Production-ready recommendations', 'Free expert consultation'].forEach(function(featureText) {
      var item = createElement('span', { className: 'pc-feature-item' });
      var checkIcon = createElement('i', { className: 'fa-solid fa-check pc-fc' });
      var labelSpan = document.createElement('span');
      labelSpan.textContent = ' ' + featureText;
      item.appendChild(checkIcon);
      item.appendChild(labelSpan);
      featuresEl.appendChild(item);
    });

    var screenEl = createElement('div', { className: 'pc-start-screen' });
    appendChildren(screenEl, [trustRow, titleEl, subtitleEl, startBtn, featuresEl]);

    /* Compact series explanation on start screen */
    var seriesNote = createElement('div', {});
    seriesNote.style.cssText = 'max-width:620px;margin:20px auto 0;text-align:center;font-size:13px;color:var(--pc-text-secondary);line-height:1.55;';
    seriesNote.innerHTML = 
      '<strong>Our four series:</strong> ' +
      '<span style="color:#3780C3;font-weight:600;">VECTOR</span> (entry) → ' +
      '<span style="color:var(--pc-accent);font-weight:600;">TITAN</span> (commercial) → ' +
      '<span style="color:#10b981;font-weight:600;">ATLAS</span> (large format) → ' +
      '<span style="color:#dc2626;font-weight:600;">APEX</span> (automation).<br>' +
      'Start small, grow, expand, then scale.';
    screenEl.appendChild(seriesNote);

    bodyEl.appendChild(screenEl);

    startBtn.addEventListener('click', function() {
      PC_STATE.step = 0;
      renderQuizView();
    });

    /* Auto-focus the start button after paint */
    setTimeout(function() {
      var btn = bodyEl.querySelector('.pc-start-btn');
      if (btn) btn.focus();
    }, 100);
  }

/* =========================================================================
   QUIZ VIEW — pill progress bar + step card, createElement-based.
    ========================================================================= */
  function renderQuizView() {
    var root = getRoot();
    if (!root) return;
    var c = PC_DOM.container || ensureContainer(root);
    var bodyEl = c.querySelector('.pc-body');
    if (!bodyEl) {
      bodyEl = createElement('div', { className: 'pc-body' });
      c.appendChild(bodyEl);
    }
    clearChildren(bodyEl);

    /* Pill progress bar — sibling of bodyEl, not inside it */
    var pillEl = createElement('div', { className: 'pc-pill-progress' });
    pillEl.style.width = '100%';
    var fillEl = createElement('div', { className: 'pc-pill-fill' });
    fillEl.style.width = '0%';
    pillEl.appendChild(fillEl);
    c.insertBefore(pillEl, bodyEl);

    buildStepBar();
    renderStep(0);
  }

/* =========================================================================
   STEP RENDERING — replaces innerHTML template with createElement chain.
   Same visual output: topbar, title, option grid, nav row.
    ========================================================================= */
  function renderStep(idx) {
    var root = getRoot();
    if (!root) return;
    var step = PC_STEPS[idx];
    if (!step) return;

    var c = PC_DOM.container || ensureContainer(root);
    var bodyEl = c.querySelector('.pc-body');
    if (!bodyEl) {
      bodyEl = createElement('div', { className: 'pc-body' });
      c.appendChild(bodyEl);
    }
    clearChildren(bodyEl);

    /* Build option cards */
    var optCards = [];
    for (var i = 0; i < step.options.length; i++) {
      var opt = step.options[i];
      var isSelected = PC_STATE.answers[step.key] === opt.value;
      var card = createElement('div', {
        className: 'pc-opt-card' + (isSelected ? ' pc-opt-sel' : ''),
        'data-value': opt.value,
        'aria-selected': isSelected ? 'true' : 'false',
        role: 'button',
        tabindex: '0'
      });

      /* Icon wrapper */
      var iconWrap = createElement('div', { className: 'pc-opt-icon-wrap' });
      var iconInner = createElement('span', { className: 'pc-opt-icon' });
      iconInner.innerHTML = opt.icon;
      iconWrap.appendChild(iconInner);
      card.appendChild(iconWrap);

      /* Label */
      var labelEl = createElement('span', { className: 'pc-opt-label' });
      labelEl.textContent = opt.label;
      card.appendChild(labelEl);

      /* Sublabel */
      var subEl = createElement('span', { className: 'pc-opt-sub' });
      subEl.textContent = opt.sublabel || '';
      card.appendChild(subEl);

      /* Check mark */
      var checkEl = createElement('span', { className: 'pc-opt-check' });
      checkEl.textContent = '+';
      card.appendChild(checkEl);

      optCards.push(card);
    }

    /* Build step card from scratch (was innerHTML .join()) */
    var backBtn = createElement('button', {
      className: 'pc-back-btn',
      textContent: 'Back ',
      disabled: idx === 0
    });
    if (idx === 0) {
      backBtn.style.opacity = '.35';
      backBtn.style.pointerEvents = 'none';
    }
    var backIcon = document.createElement('i');
    backIcon.className = 'fa-solid fa-arrow-left';
    backBtn.appendChild(backIcon);

    var counterEl = createElement('span', {
      className: 'pc-step-counter',
      textContent: (idx + 1) + ' / ' + PC_STEPS.length
    });

    var intBadge = createElement('span', { className: 'pc-int-badge' });
    intBadge.innerHTML = '<i class="fa-solid fa-bolt"></i> Intelligent Match';

    var topbar = createElement('div', { className: 'pc-step-topbar' });
    topbar.appendChild(backBtn);
    topbar.appendChild(counterEl);
    topbar.appendChild(intBadge);

    var titleEl = createElement('h3', {
      className: 'pc-step-title',
      textContent: step.title
    });

    /* Nav row */
    var nextBtn = createElement('button', {
      className: 'pc-btn-next pc-btn-grad',
      textContent: (idx === PC_STEPS.length - 1) ? 'See Results ' : 'Continue ',
      disabled: false /* single-select always enabled after selection */
    });
    var nextArrow = document.createElement('i');
    nextArrow.className = 'fa-solid fa-arrow-right';
    nextBtn.appendChild(nextArrow);

    var navRow = createElement('div', { className: 'pc-nav-row' });
    navRow.appendChild(backBtn);
    navRow.appendChild(nextBtn);

    var stepCard = createElement('div', {
      className: 'pc-step-card',
      tabindex: '-1'
    });
         /* Option grid wrapper — CSS grid rules target .pc-opt-grid */
      var optGrid = createElement('div', { className: 'pc-opt-grid' });
      for (var gi = 0; gi < optCards.length; gi++) {
        optGrid.appendChild(optCards[gi]);
        }

    appendChildren(stepCard, [topbar, titleEl, optGrid, navRow]);
    bodyEl.appendChild(stepCard);

    /* Wire up option card clicks */
    for (var ci = 0; ci < optCards.length; ci++) {
      (function(card) {
        card.addEventListener('click', function() {
          handleOptionClick(step, card.getAttribute('data-value'), card);
        });
        /* Keyboard support: Enter/Space triggers click */
        card.addEventListener('keydown', function(e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            card.click();
          }
        });
      })(optCards[ci]);
    }

    /* Back button */
    if (idx > 0) {
      backBtn.addEventListener('click', function() {
        PC_STATE.step = idx - 1;
        renderStep(idx - 1);
      });
    }

    /* Next button — auto-advance for single-select steps */
    nextBtn.addEventListener('click', function() {
      var currentStepKey = step.key;
      if (step.type === 'single' && !PC_STATE.answers[currentStepKey]) return;
      PC_STATE.step = idx + 1;
      if (PC_STATE.step >= PC_STEPS.length) {
        handleResults();
      } else {
        renderStep(PC_STATE.step);
      }
    });

    /* Pill progress update — query from root, pill is sibling of bodyEl */
    var pillFill = getRoot().querySelector('.pc-pill-fill');
    if (pillFill) {
      var pct = Math.round((idx / PC_STEPS.length) * 100);
      pillFill.style.width = pct + '%';
    }

    /* Tab highlight */
    updateTabHighlight(idx);

    /* Auto-focus step card for keyboard nav */
    setTimeout(function() {
      stepCard.focus();
    }, 80);

    /* === Swipe gestures for quiz steps on mobile === */
    if (window.innerWidth < 768) {
      let touchStartX = 0;
      let touchEndX = 0;

      const handleSwipe = () => {
        const delta = touchEndX - touchStartX;
        if (Math.abs(delta) > 55) {
          if (delta < 0 && idx < PC_STEPS.length - 1) {
            // Swipe left → Next question
            renderStep(idx + 1);
          } else if (delta > 0 && idx > 0) {
            // Swipe right → Previous question
            renderStep(idx - 1);
          }
        }
      };

      stepCard.addEventListener('touchstart', function(e) {
        touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });

      stepCard.addEventListener('touchend', function(e) {
        touchEndX = e.changedTouches[0].screenX;
        handleSwipe();
      }, { passive: true });
    }
  }

  function clearChildren(el) {
    while (el.firstChild) el.removeChild(el.firstChild);
    return el;
  }

/* =========================================================================
   OPTION HANDLING — single-select auto-advance, multi-select toggle.
   Short-circuits to results when useCase has < 4 matching products.
    ========================================================================= */
  function handleOptionClick(step, value, cardEl) {
    if (step.type === 'single') {
      /* Clear previous selection in this step */
      var siblings = cardEl.parentElement.querySelectorAll('.pc-opt-card');
      for (var i = 0; i < siblings.length; i++) {
        siblings[i].classList.remove('pc-opt-sel');
        siblings[i].setAttribute('aria-selected', 'false');
      }
      /* Mark selected */
      cardEl.classList.add('pc-opt-sel');
      cardEl.setAttribute('aria-selected', 'true');

      PC_STATE.answers[step.key] = value;
        /* Check if useCase has few matches — show skip popup (EXCLUSIVE path) */
        if (step.key === 'useCase') {
          var matches = [];
          if (value === 'high-volume') {
            PC_STATE.products.forEach(function(p) {
              if (isHighVolumeSKU(p.sku)) matches.push(p);
                });
              } else {
            matches = PC_STATE.products.filter(function(p) {
              return p.useCase === value && isHeatPress(p);
                });
              }

          var matchCount = matches.length;
          // Debug: count products by useCase
          var ucCounts = {};
          for (var _i = 0; _i < PC_STATE.products.length; _i++) {
            var _uc = PC_STATE.products[_i].useCase || 'unknown';
            ucCounts[_uc] = (ucCounts[_uc] || 0) + 1;
          }
          if (matchCount < 4) {
            var skipTimeout = setTimeout(function() {
              PC_STATE.step++;
              if (PC_STATE.step >= PC_STEPS.length) handleResults();
              else renderStep(PC_STATE.step);
            }, 3500);
            showSkipPopup(value, matchCount, skipTimeout);
            return;
          }
        }

        /* Normal auto-advance (only when skip popup was NOT shown) */
        setTimeout(function() {
          cardEl.style.transform = '';
          PC_STATE.step++;
          if (PC_STATE.step >= PC_STEPS.length) handleResults();
          else renderStep(PC_STATE.step);
           }, 320);

    } else {
      /* Multi-select: toggle value in answers[step.key + '-list'] */
      var wasSelected = cardEl.classList.contains('pc-opt-sel');
      var listKey = step.key + '-list';
      var curList = PC_STATE.answers[listKey] || [];

      if (wasSelected) {
        cardEl.classList.remove('pc-opt-sel');
        cardEl.setAttribute('aria-selected', 'false');
        PC_STATE.answers[listKey] = curList.filter(function(v) { return v !== value; });
      } else {
        if (curList.length >= (step.maxSelect || Infinity)) return;
        cardEl.classList.add('pc-opt-sel');
        cardEl.setAttribute('aria-selected', 'true');
        curList.push(value);
        PC_STATE.answers[listKey] = curList;
      }

      /* Enable/disable next button based on selection count */
      var nextBtn = cardEl.closest('.pc-step-card').querySelector('.pc-btn-next');
      if (nextBtn) {
        nextBtn.disabled = (PC_STATE.answers[listKey] || []).length === 0;
      }
    }
  }

  /* Skip popup: shown when useCase has < 4 matching products.
     Pattern: fixed overlay + centered box with CTA button. */
  function showSkipPopup(value, matchCount, skipTimeout) {
    var root = getRoot();
    if (!root) return;

    var overlay = createElement('div', { id: 'pc-skip-ov' });
    overlay.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,.6);z-index:10000;display:flex;align-items:center;justify-content:center;';

    var box = createElement('div', { className: 'pc-skip-box' });
    box.style.cssText = 'position:fixed;top:50%;left:50%;transform:translate(-50%, -50%);background:var(--pc-surface);border:1px solid var(--pc-border);border-radius:var(--pc-radius);padding:32px;max-width:420px;width:90%;text-align:center;z-index:10001;box-shadow:0 20px 60px rgba(0,0,0,.5);';

    /* Close X button */
    var closeBtn = createElement('button', {
      className: 'pc-skip-x',
      textContent: '\u00D7',
      id: 'pc-skip-close-btn'
    });
    closeBtn.style.cssText = 'position:absolute;top:12px;right:14px;background:none;border:none;color:var(--pc-text-secondary);font-size:20px;cursor:pointer;width:28px;height:28px;display:flex;align-items:center;justify-content:center;border-radius:50%;transition:background .2s;';
    box.appendChild(closeBtn);

    /* Sparkle icon */
    var icon = createElement('span', { className: 'pc-skip-icon pc-skip-icon-sparkles' });
    icon.style.cssText = 'font-size:36px;display:block;margin-top:4px;';
    box.appendChild(icon);

    /* Title */
    var title = createElement('h3', {
      className: '',
      textContent: matchCount + (matchCount === 1 ? ' Machine' : ' Machines') + ' Matches'
    });
    title.style.cssText = 'font-size:20px;font-weight:700;color:var(--pc-text);margin:16px 0 8px;line-height:1.3;';
    box.appendChild(title);

    /* Subtitle */
    var subtitle = createElement('p', { className: '' });
    subtitle.style.cssText = 'font-size:14px;color:var(--pc-text-secondary);margin:0 0 20px;line-height:1.5;';
    subtitle.innerHTML = 'The best options for <b>' + value + '</b> are limited.';
    box.appendChild(subtitle);

    /* Action buttons */
    var actionsDiv = createElement('div', { className: 'pc-skip-actions' });
    actionsDiv.style.cssText = 'display:flex;gap:10px;justify-content:center;flex-wrap:wrap;';

    var goBtn = createElement('button', {
      className: 'pc-btn pc-btn-grad',
      textContent: 'See Machines ',
      id: 'pc-skip-go'
    });
    var goArrow = document.createElement('i');
    goArrow.className = 'fa-solid fa-arrow-right';
    goBtn.appendChild(goArrow);

    actionsDiv.appendChild(goBtn);
    box.appendChild(actionsDiv);
    overlay.appendChild(box);

    ((parent) => {
      parent.appendChild(overlay);
    })(root.parentElement || document.body);

    /* Close handlers */
    overlay.addEventListener('click', function(e) {
      if (e.target === overlay) overlay.remove();
    });
    closeBtn.addEventListener('click', function() { overlay.remove(); });

    setTimeout(function() {
      goBtn.addEventListener('click', function() {
        clearTimeout(skipTimeout);
        overlay.remove();
        renderResultsPage();
      });
    }, 30);
  }

/* =========================================================================
   STEP BAR — horizontal tab bar below nav header.
   createElement-based replacement for innerHTML template string.
    ========================================================================= */
  function buildStepBar() {
    var root = getRoot();
    if (!root) return;
    if (root.querySelector('.pc-step-bar-wrap')) return; /* Already built */

    var tabLabels = {
      useCase: 'Application',
      volume: 'Volume',
      budget: 'Budget',
      automation: 'Automation',
      voltage: 'Power'
    };

    var barEl = createElement('div', { className: 'pc-step-bar-wrap' });

    var tabBar = createElement('div', { className: 'pc-step-tab-bar' });

    /* Step counter label */
    var tabLabel = createElement('span', {
      className: 'pc-step-tab-label pc-tab-total',
      textContent: 'STEP 1 OF ' + PC_STEPS.length
    });
    tabBar.appendChild(tabLabel);

    /* Individual step tabs — built via createElement, not innerHTML */
    for (var i = 0; i < PC_STEPS.length; i++) {
      var s = PC_STEPS[i];
      var label = tabLabels[s.key] || s.title.split(' ')[0];
      var tab = createElement('span', {
        className: 'pc-step-tab',
        textContent: label
      });
      tabBar.appendChild(tab);
    }

    barEl.appendChild(tabBar);

    /* Insert after nav header, before container */
    var navHeader = root.querySelector('.pc-nav-header');
    if (navHeader) {
      root.insertBefore(barEl, navHeader.nextSibling);
    } else {
      root.appendChild(barEl);
    }

    updateTabHighlight(0);
  }

  function updateTabHighlight(idx) {
    var root = getRoot();
    if (!root) return;
    var bar = root.querySelector('.pc-step-bar-wrap');
    if (!bar) return;
    var tabs = bar.querySelectorAll('.pc-step-tab');
    if (!tabs.length) return;

    var total = (idx >= 0 && idx < tabs.length) ? idx + 1 : tabs.length;
    for (var i = 0; i < tabs.length; i++) {
      tabs[i].classList.toggle('pc-tab-active', i === Math.min(idx, tabs.length - 1));
    }

    var totalLabel = bar.querySelector('.pc-tab-total');
    if (totalLabel) totalLabel.textContent = 'STEP ' + total + ' OF ' + tabs.length;

    bar.style.display = (idx >= tabs.length) ? 'none' : '';
    bar.style.opacity = (idx >= tabs.length) ? '.35' : '1';
  }

/* =========================================================================
   NAV HEADER — persistent top bar with brand + quit button.
    ========================================================================= */
  function buildNavHeader(root) {
    var nv = createElement('nav', { className: 'pc-nav-header' });

    var inner = createElement('div', { className: 'pc-nav-inner' });

    /* Brand */
    var brand = createElement('div', { className: 'pc-nav-brand' });
    var brandIcon = createElement('div', { className: 'pc-brand-icon' });
    brandIcon.style.cssText = 'width:36px;height:36px;background:var(--pc-primary);border-radius:10px;display:flex;align-items:center;justify-content:center;font-size:18px;color:#fff;';
    brandIcon.innerHTML = '<svg viewBox="0 0 48 48" width="28" height="28" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="10" y="24" width="28" height="10" rx="3" fill="currentColor"/><rect x="12" y="14" width="24" height="8" rx="2" fill="currentColor"/><rect x="20" y="6" width="8" height="10" rx="3" fill="currentColor"/><path d="M14 18h20" stroke="white" stroke-width="2" stroke-linecap="round"/></svg>';

    var brandText = createElement('div', {});
    var titleSpan = createElement('span', { className: 'pc-nav-title' });
    titleSpan.textContent = 'The Heat Press Finder';
    var subtitleSpan = createElement('span', { className: 'pc-nav-subtitle' });
    subtitleSpan.textContent = 'POWERED BY MICROTEC USA';
    subtitleSpan.style.cssText = 'display:block;text-align:left;';
    brandText.appendChild(titleSpan);
    brandText.appendChild(subtitleSpan);
    brand.appendChild(brandIcon);
    brand.appendChild(brandText);
    inner.appendChild(brand);

    /* Right side */
    var rightDiv = createElement('div', { className: 'pc-nav-right' });
    var badge = createElement('span', { className: 'pc-nav-badge' });
    badge.innerHTML = '<i class="fa-solid fa-check"></i> AI-Powered Quiz';
    badge.style.display = window.innerWidth < 768 ? 'none' : ''; // Hide on mobile

    var quitBtn = createElement('button', {
      className: 'pc-quit-btn',
      id: 'pc-quit-btn'
     });
    quitBtn.setAttribute('title', 'Quit quiz');
    quitBtn.addEventListener('click', function() { window.PC_INIT(); });
    quitBtn.innerHTML = '\u00D7';

    var quitLabel = createElement('span', { className: 'pc-quit-label' });
    quitLabel.textContent = 'Restart';
    quitLabel.style.cssText = 'cursor:pointer;';

    var quitWrap = createElement('span', { className: 'pc-quit-wrap' });
    quitWrap.appendChild(quitBtn);
    quitWrap.appendChild(quitLabel);
    quitWrap.addEventListener('click', function() { window.PC_INIT(); });

    rightDiv.appendChild(badge);
    rightDiv.appendChild(quitWrap);
    inner.appendChild(rightDiv);
    nv.appendChild(inner);
    root.appendChild(nv);
  }

/* =========================================================================
   DEBUG — dump per-product scores to console, sorted descending.
       ======================================================================== */
  function dumpScores() {
    var products = PC_STATE.products || [];
    if (!products.length) return;
    var answers = PC_STATE.answers || {};

      /* Score every product against current answers using new points-based system */
    var scored = computeScores(products, answers);

       /* Log all products with scores, top 20 min-width */
    var shown = Math.min(20, scored.length);
    for (var k = 0; k < shown; k++) {
      var item = scored[k];
    }

       /* Budget details for top-3 scored products */
    if (answers.budget) {
      var filtered = applyBudgetFilter(answers.budget, products);
      for (var m = 0; m < Math.min(3, shown); m++) {
        var tp = scored[m];
      }
    }

  }

/* =========================================================================
   SCORING ENGINE — weighted multi-criteria decision model.
   Each step contributes: matchScore x weight, with budget/voltage adjustments.
     ======================================================================== */
function scoreProduct(product, answers) {
    /* This function is deprecated - replaced by computeScores() */
    return 0;
  }

  function getStepScore(product, step, answer) {
    if (product.title && product.title.indexOf("VECTOR") === 0 && product.title.indexOf("MUG") !== 0) {
    }

    switch (step.key) {
      case 'useCase':
        var ucmatch = product.useCase === answer;
        if (ucmatch) return 1.0;
        /* Specialized products get small credit for apparel users */
        if (['mugs', 'caps', 'sports-balls'].indexOf(product.useCase) !== -1 && answer === 'apparel') return 0.05;
        /* Apparel gets tiny credit for specialized users */
        if (['mugs', 'caps', 'sports-balls', 'large-format'].indexOf(answer) !== -1) return 0.02;
        return 0.0;

      case 'volume': {
        var capMap = { low: 'entry', medium: 'medium', high: 'pro', industrial: 'industrial' };
        var target = capMap[answer];
        if (product.capacity === target) return 1.0;
        var tiers = ['entry', 'medium', 'pro', 'industrial'];
        var pIdx = tiers.indexOf(product.capacity);
        var tIdx = tiers.indexOf(target);
        if (Math.abs(pIdx - tIdx) === 1) return 0.3;
        return 0.0;
      }

      case 'budget': {
        var price = product.price || 0;
        var ranges = BUDGET_RANGES;
        var range = ranges[answer];
        if (!range) return 0.0;
          /* In budget: full match */
        if (price >= range[0] && price <= range[1]) return 1.0;
        /* Soft edge: within $750 of range */
        /* Within $750 of range: graduated credit */
        var pctOff = (price > range[1]) ? (price - range[1]) / Math.max(1, range[1]) : (range[0] - price) / Math.max(1, range[0]);
        if (pctOff <= 0.25) return 0.4;
          /* Over budget: graduated penalty — never fully zero out */
        var overPct = (price - range[1]) / range[1];
        if (overPct > 0.5) return 0.1;
        if (overPct > 0.25) return 0.3;
      }

      case 'automation': {
        var method = ((product && product.method) || '').toLowerCase();
        if (answer === 'manual') {
          if (method.indexOf('manual') !== -1 || method.indexOf('hand') !== -1) return 1.0;
          if (method.indexOf('automatic') === -1 && method.indexOf('pneumatic') === -1) return 0.7;
          return 0.2;
        }
        if (answer === 'auto-open') {
          if (method.indexOf('auto') !== -1 || method.indexOf('electromagnetic') !== -1 || method.indexOf('spring') !== -1) return 1.0;
          return 0.1;
        }
        if (answer === 'pneumatic') {
          if (method.indexOf('pneumatic') !== -1 || method.indexOf('air-powered') !== -1 || method.indexOf('air-driven') !== -1) return 1.0;
          var capVal = parseFloat(product.capacity) || 0;
          if (capVal >= 50 && capVal < 150) return 0.5;
          return 0.1;
        }
        if (answer === 'rotary-auto') {
          if (method.indexOf('rotary') !== -1 || method.indexOf('automatic') !== -1 || method.indexOf('roll-to-roll') !== -1) return 1.0;
          if (product.workstationCount && parseInt(product.workstationCount) >= 3) return 0.8;
          return 0.1;
        }
        return 0.1;
      }

      case 'voltage': {
        var vMap = { '120v': '110v', '240v': '220v', '3-phase': '3phase' };
        var ansNorm = vMap[answer] || '110v';
        var prodNorm = getVoltageNormalized(product);
        var voltOrder = ['110v', '220v', '3phase'];
        if (prodNorm === ansNorm) return 1.0;
        var aIdx = voltOrder.indexOf(ansNorm);
        var pIdx = voltOrder.indexOf(prodNorm);
        if (aIdx >= 0 && pIdx >= 0 && Math.abs(aIdx - pIdx) === 1) return 0.5;
        return 0.0;
      }

      default: return 0;
    }
  }

  function applyBudgetPenalty(score, product, answers) {
    var budgetKey = answers.budget;
    if (!budgetKey) return score;
    var price = product.price || 0;
    var range = BUDGET_RANGES[budgetKey];
    if (!range) return score;

    /* Within or under budget: no penalty */
    if (price <= range[1]) return score;

       /* Over budget: graduated penalty */
    var overPct = (price - range[1]) / range[1];
    if (overPct > 0.5) return Math.max(0.05, score * 0.2);
    if (overPct > 0.25) return score * 0.4;
    return score * 0.7;
  }

/* =========================================================================
   NEW SCORING ENGINE — points-based model. Each step awards discrete points.
   No dot products. Total max = 95 points (20+15+25+23+12).
     ======================================================================== */

  /* Helper: Returns true if this product should NEVER appear for Apparel answers */
  function isApparelForbidden(product) {
    if (!product) return false;
    var t = (product.title || '').toUpperCase();
    var keywords = ['CAP', 'MUG', 'TUMBLER', 'BALL', 'DRINKWARE', 'MUG PRESS', 'TUMBLER PRESS'];
    for (var i = 0; i < keywords.length; i++) {
      if (t.indexOf(keywords[i]) !== -1) return true;
    }
    // Also check derived useCase as final safety
    if (product.useCase === 'caps' || product.useCase === 'mugs' || 
        product.useCase === 'sports-balls') return true;
    return false;
  }

    /* Q1: useCase - binary gate. Matching category = +20pts, non-matching specialized = +2pts for apparel users. */
  function scoreQ1UseCase(product, answer) {
    var t = (product.title || '').toUpperCase();

    /* High-volume DTF: should show X4/X6/X8, XR1515/XR1620 as top results */
    if (answer === 'high-volume') {
      // EXTREMELY STRICT: High-Volume DTF should ONLY show APEX machines
      if (product.series !== 'APEX') return 0;
      var hvResult = isHighVolumeSKU(product.sku);
      return hvResult ? 20 : 15; // Still give some points to APEX even if not exact SKU
    }

    /* Multi-product shop: should show VECTOR/TITAN dual-station presses */
    if (answer === 'apparel' && !isApparelForbidden(product) && (t.indexOf('DUAL') !== -1 || t.indexOf('STATION') !== -1)) {
      return 20; /* VECTOR/TITAN dual-station gets priority */
    }

    /* Standard garments (apparel) — more specific & practical matching:
       - Strictly exclude specialized categories
       - 20pts for common garment platens (~200-450 sq in covers real t-shirt/hoodie presses)
       - 18pts for other valid apparel presses (still excellent)
       - +2pt bonus for VECTOR/TITAN (most reliable for standard production) */
    if (answer === 'apparel') {
      /* EXTREMELY STRICT apparel matching for Q1 */
      if (isApparelForbidden(product)) return -100;   // Force out mug/cap/ball/tumbler presses

      var area = calcPlatenArea(product.platenW, product.platenH);
      if (!area || area <= 0) return 0;               // Must have real platen

      // Deprioritize ATLAS machines heavily for apparel (large format)
      if (product.series === 'ATLAS') return 3;       // Very low ranking, but not completely hidden

      if (product.useCase === 'caps' || product.useCase === 'mugs' || 
          product.useCase === 'large-format' || product.useCase === 'sports-balls') {
        return 0;
      }

      var base = (area >= 200 && area <= 450) ? 20 : 18;
      if (product.series === 'VECTOR' || product.series === 'TITAN') {
        base = Math.min(22, base + 2);
      }
      return base;
    }

    /* Caps & headwear: should show ONLY cap presses (no apparel) */
    if (answer === 'caps') {
      return product.useCase === 'caps' ? 20 : 0;
    }

    /* Mugs & drinkware: should show ONLY mug presses (no apparel) */
    if (answer === 'mugs') {
      return product.useCase === 'mugs' ? 20 : 0;
    }

    /* Sports balls & specialty: should show ONLY products with "Ball" in title */
    if (answer === 'sports-balls') {
      return t.indexOf('BALL') !== -1 ? 20 : 0;
    }

    /* Large format: should show ONLY ATLAS machines (no other series) */
    if (answer === 'large-format') {
      return product.series === 'ATLAS' ? 20 : 0;
    }

    return 0;
  }

    /* Q2: volume - tiered capacity match. Exact = +15pts, adjacent tier = +7pts, two-tier gap = +2pts.
       Capacity metafield is usually numeric (pieces/hour), so map it to a tier first:
       <25/hr = entry, 25-59 = medium, 60-149 = pro, 150+ = industrial. */
  function scoreQ2Volume(product, answer) {
    var capMap = { low: 'entry', medium: 'medium', high: 'pro', industrial: 'industrial' };
    var target = capMap[answer];
    if (!target) return 0;
    var tiers = ['entry', 'medium', 'pro', 'industrial'];

    var raw = (product.capacity || '').toString().toLowerCase().trim();
    var prodTier = tiers.indexOf(raw) !== -1 ? raw : null;
    if (!prodTier) {
      var perHour = parseFloat(raw.replace(/[^0-9.]/g, ' '));
      if (!isNaN(perHour) && perHour > 0) {
        prodTier = perHour < 25 ? 'entry' : perHour < 60 ? 'medium' : perHour < 150 ? 'pro' : 'industrial';
      }
    }
    if (!prodTier) return 7; /* Unknown capacity: neutral credit, don't punish */

    var gap = Math.abs(tiers.indexOf(prodTier) - tiers.indexOf(target));
    if (gap === 0) return 15;
    if (gap === 1) return 7;
    if (gap === 2) return 2;
    return 0;
  }

    /* Q3: budget - HARD FILTER applied after scoring. Not a scoring step. */
  function applyBudgetFilter(budgetKey, products) {
    if (!budgetKey) return products;
    var range = BUDGET_RANGES[budgetKey];
    if (!range) return products;

    var filtered = [];
    for (var i = 0; i < products.length; i++) {
      var product = products[i];
      var price = product.price || 0;
      /* HARD CAP: only include products at or under the exact selected budget maximum (cents from Shopify) */
      if (price <= range[1]) {
        filtered.push(product);
      }
    }
    return filtered;
  }

    /* Q4: features - title/description/metafield matching. Case-insensitive throughout. */
  function scoreQ4Features(product, answer) {
    var score = 0;
    var title = (product.title || '').toUpperCase();
    var desc = (product.description || '').toUpperCase();

    /* Manual */
    if (answer === 'manual') {
      if (title.indexOf('MANUAL') !== -1 || desc.indexOf('MANUAL') !== -1) score += 8;
      if (title.indexOf('HAND') !== -1 || desc.indexOf('HAND') !== -1) score += 5;
    }

    /* Auto-open */
    if (answer === 'auto-open') {
      if (title.indexOf('AUTO') !== -1 || desc.indexOf('AUTO') !== -1) score += 8;
      if (title.indexOf('ELECTROMAGNETIC') !== -1 || desc.indexOf('ELECTROMAGNETIC') !== -1) score += 5;
      if (title.indexOf('SPRING') !== -1 || desc.indexOf('SPRING') !== -1) score += 5;
    }

    /* Pneumatic */
    if (answer === 'pneumatic') {
      if (title.indexOf('PNEUMATIC') !== -1 || desc.indexOf('PNEUMATIC') !== -1) score += 8;
      if (title.indexOf('AIR') !== -1 || desc.indexOf('AIR') !== -1) score += 5;
    }

    /* Rotary-auto */
    if (answer === 'rotary-auto') {
      if (title.indexOf('ROTARY') !== -1 || desc.indexOf('ROTARY') !== -1) score += 8;
      if (title.indexOf('ROLL') !== -1 || desc.indexOf('ROLL') !== -1) score += 5;
      if (title.indexOf('AUTO') !== -1 || desc.indexOf('AUTO') !== -1) score += 5;
    }

    return Math.min(score, 23); /* Max 23 points */
  }

    /* Q5: voltage - hard elimination with compatibility matrix.
      3-phase -> shows all voltages (most permissive).
      240V -> shows 120V + 240V, eliminates 3-phase-only.
      120V -> only 120V machines shown. */
  function getVoltageNormalized(product) {
    var v = (product.voltageRaw || '').toLowerCase();
    if (v.indexOf('3-phase') !== -1 || v.indexOf('3 phase') !== -1) return '3phase';
    if (v.indexOf('240') !== -1 || v.indexOf('220') !== -1) return '240v';
    if (v.indexOf('120') !== -1 || v.indexOf('110') !== -1) return '120v';
    return '120v';
  }

  function scoreQ5Voltage(product, userVoltage) {
    if (!userVoltage) return 0;
    var prodNorm = getVoltageNormalized(product);

    /* 3-phase: most permissive - shows everything */
    if (userVoltage === '3-phase') {
      return 12; /* All voltages compatible */
    }

    /* 240V: shows 120V + 240V, eliminates 3-phase-only */
    if (userVoltage === '240v') {
      if (prodNorm === '120v' || prodNorm === '240v') return 12;
      return 0; /* 3-phase eliminated */
    }

    /* 120V: only shows 120V machines */
    if (userVoltage === '120v') {
      return prodNorm === '120v' ? 12 : 0;
    }

    return 0;
  }

  /* Voltage HARD FILTER: a 120V-only shop physically cannot run 240V or 3-phase
     machines, and a 240V shop cannot run 3-phase-only. 3-phase shops run everything. */
  function applyVoltageFilter(voltageKey, products) {
    if (!voltageKey) return products;
    return products.filter(function(p) {
      var v = getVoltageNormalized(p);
      if (voltageKey === '120v') return v === '120v';
      if (voltageKey === '240v') return v !== '3phase';
      return true;
    });
  }

  /* Combined hard filters - anything that survives is physically usable AND affordable. */
  function applyHardFilters(answers, products) {
    return applyVoltageFilter(answers.voltage, applyBudgetFilter(answers.budget, products));
  }

    /* Full scoring entry point - replaces old scoreProduct() entirely. */
  function computeScores(products, answers) {
    var scored = [];

    /* Apply HARD budget cap first (price <= selected max in cents) */
    /* Apply HARD filters first: budget cap (price <= selected max in cents) + voltage compatibility */
    var filteredProducts = applyHardFilters(answers, products);

    for (var i = 0; i < filteredProducts.length; i++) {
      var product = filteredProducts[i];
      var points = 0;

      /* Q1: useCase (20 points max) */
      points += scoreQ1UseCase(product, answers.useCase);

      /* Q2: volume (15 points max) */
      points += scoreQ2Volume(product, answers.volume);

      /* Q3: budget - HARD FILTER already applied; all remaining products are within budget cap → award full points */
      if (answers.budget) {
        var bRange = BUDGET_RANGES[answers.budget];
        points += (bRange && (product.price || 0) >= bRange[0]) ? 25 : 15;
      }

      /* Q4: features (23 points max) */
      points += scoreQ4Features(product, answers.automation);

      /* Q5: voltage (12 points max) */
      points += scoreQ5Voltage(product, answers.voltage);

      scored.push({ product: product, score: points });
    }

    /* Sort by score descending */
    scored.sort(function(a, b) { return b.score - a.score; });

    return scored;
  }

/* =========================================================================
   MATCHED COUNT — quick filter for useCase display text.
    ========================================================================= */
  function countByUseCase(products, useCase) {
    var count = 0;
    for (var i = 0; i < products.length; i++) {
      if (products[i].useCase === useCase) count++;
    }
    return count;
  }

/* =========================================================================
   RESULTS PAGE — best-match card + alternatives + series grid + table.
   Single entry point, composes smaller render functions.
    ========================================================================= */
  function handleResults() {
    PC_STATE.step = PC_STEPS.length;
    renderResultsPage();
  }

  function renderResultsPage() {
    var root = getRoot();
    if (!root) return;
    var c = PC_DOM.container || ensureContainer(root);
    var bodyEl = c.querySelector('.pc-body');
    if (!bodyEl) {
      bodyEl = createElement('div', { className: 'pc-body' });
      c.appendChild(bodyEl);
    }
    clearChildren(bodyEl);

    /* Score all products using new points-based system */
    var scored = computeScores(PC_STATE.products, PC_STATE.answers);

    PC_STATE.scored = scored;

    /* Separate matched / unmatched by useCase */
    var isHighDemand = (
      (PC_STATE.answers.volume === 'high' || PC_STATE.answers.volume === 'industrial') ||
      (PC_STATE.answers.budget === 'premium' || PC_STATE.answers.budget === 'enterprise') ||
      PC_STATE.answers.voltage === '3-phase'
    );

var matched = [], unmatched = [];
    for (var mi = 0; mi < scored.length; mi++) {
      var prod = scored[mi].product;
      var uc = PC_STATE.answers.useCase;
      var isMatch = false;
      if (uc) {
          /* High-Volume DTF: ONLY APEX machines (strict) */
        if (uc === 'high-volume' && (prod.series === 'APEX' || isHighVolumeSKU(prod.sku))) {
          isMatch = true;
        }

        /* Large Format: ONLY ATLAS machines (strict) */
        if (uc === 'large-format' && prod.series === 'ATLAS') isMatch = true;
         /* Standard useCase match for other categories */
        if (!isMatch && prod.useCase === uc && isHeatPress(prod)) {
          /* Extra strict filter for apparel */
          if (uc === 'apparel') {
            var a = calcPlatenArea(prod.platenW, prod.platenH);
            var titleUpper = (prod.title || '').toUpperCase();

            // Must have platen + must NOT contain forbidden keywords (very aggressive)
            var forbidden = ['CAP', 'MUG', 'TUMBLER', 'BALL', 'DRINKWARE'];
            var hasForbidden = forbidden.some(function(word) {
              return titleUpper.indexOf(word) !== -1;
            });
            if (a && a > 0 && !hasForbidden) {
              isMatch = true;
            }
          } else {
            isMatch = true;
          }
        }
        }
      if (isMatch) matched.push(scored[mi]);
      else if (uc) unmatched.push(scored[mi]);
     }

    /* No useCase answered (tiny catalogs render results directly):
       everything that survived the hard filters counts as matched. */
    if (!PC_STATE.answers.useCase) {
      matched = scored.slice();
      unmatched = [];
    }

    /* FINAL HARD FILTER for apparel — force out any remaining forbidden presses */
    if (PC_STATE.answers.useCase === 'apparel') {
      matched = matched.filter(function(item) {
        return !isApparelForbidden(item.product);
      });
    }

    /* EXTREMELY STRICT: High-Volume DTF → ONLY APEX machines */
    if (PC_STATE.answers.useCase === 'high-volume') {
      matched = matched.filter(function(item) {
        var p = item.product;
        return p.series === 'APEX' || isHighVolumeSKU(p.sku);
      });
    }

    /* EXTREMELY STRICT: Large Format → ONLY ATLAS */
    if (PC_STATE.answers.useCase === 'large-format') {
      matched = matched.filter(function(item) {
        return item.product.series === 'ATLAS';
      });
    }




     /* Build top-4 with series diversity (1 featured + 3 alternatives) */
    var top4 = [];
    var seriesCounts = {};

    var maxPerSeries = (PC_STATE.answers.useCase === 'high-volume') ? 5 : 2;
    for (var ti = 0; ti < matched.length; ti++) {
      if (top4.length >= 4) break;
      var p = matched[ti].product;
      var uc = PC_STATE.answers.useCase;

      // Extremely strict category enforcement
      if (uc === 'apparel' && isApparelForbidden(p)) continue;
      if (uc === 'high-volume' && (p.series !== 'APEX' && !isHighVolumeSKU(p.sku))) continue;
      if (uc === 'large-format' && p.series !== 'ATLAS') continue;

      var series = p.series || 'VECTOR';
      if (!seriesCounts[series]) seriesCounts[series] = 0;
      if (seriesCounts[series] < maxPerSeries && matched[ti].score > 0) {
        top4.push(matched[ti]);
        seriesCounts[series] = (seriesCounts[series] || 0) + 1;
      }
    }

    /* Fill from unmatched only if more matched than slots */
    /* Fill remaining slots from MATCHED overflow only (series-diversity cap relaxed).
       Never pull from unmatched - those products failed the user's criteria. */
    for (var ui = 0; ui < matched.length && top4.length < 4; ui++) {
      var exists = top4.some(function(r) { return r.product.id === matched[ui].product.id; });
      if (!exists) top4.push(matched[ui]);
    }

    PC_STATE.top4 = top4;

    /* Render results */
    renderResultsHeader(bodyEl, matched.length);
    if (top4.length > 0) {
      // Final safety for strict categories
      var best = top4[0];
      var uc = PC_STATE.answers.useCase;
      if (uc === 'apparel' && isApparelForbidden(best.product) && top4.length > 1) {
        best = top4[1];
      }
      if (uc === 'high-volume' && (best.product.series !== 'APEX' && !isHighVolumeSKU(best.product.sku)) && top4.length > 1) {
        best = top4.find(function(x){ return x.product.series === 'APEX' || isHighVolumeSKU(x.product.sku); }) || top4[1];
      }
      if (uc === 'large-format' && best.product.series !== 'ATLAS' && top4.length > 1) {
        best = top4.find(function(x){ return x.product.series === 'ATLAS'; }) || top4[1];
      }
      renderBestMatchCard(bodyEl, best, scored, matched.length);
    }

    /* Prepare full lineup for Compare All Machines */
    /* Prepare lineup for Compare All Machines - hard-filtered so a $1,000-budget
       or 120V-only user never sees machines they can't buy or run. */
    var allLineup = (PC_STATE.products || []).filter(function(p) {
      return ['VECTOR', 'TITAN', 'ATLAS', 'APEX'].indexOf(p.series) !== -1;
    });
    allLineup = applyHardFilters(PC_STATE.answers, allLineup);

    /* If only one result (e.g. specialized ball press), show ONLY the large best-match card */
    if (matched.length > 1) {
      renderMatchedTable(bodyEl, matched);
      renderSeriesComparison(bodyEl, allLineup);
      renderComparisonTable(bodyEl, allLineup);
    } else if (matched.length === 0 && PC_STATE.answers && Object.keys(PC_STATE.answers).length >= 1) {
      renderZeroResultsSupport(bodyEl);
    }

    renderCTARow(bodyEl, root);
    renderRestartSection(bodyEl);

      /* Launch confetti on results reveal */
    setTimeout(launchConfetti, 500);

     /* Hide pill bar on results page */
    var pillBar = c.querySelector('.pc-pill-progress');
    if (pillBar) pillBar.style.display = 'none';

    updateTabHighlight(PC_STEPS.length);
  }

  function renderResultsHeader(parent, matchedCount) {
    var header = createElement('div', { className: 'pc-results-header' });
    header.style.cssText = 'text-align:center;margin-bottom:32px;';

    /* Beautiful personalized header */
    var h3 = createElement('h3', {});
    h3.style.cssText = 'font-size:clamp(34px,5.5vw,52px);font-weight:800;margin-bottom:8px;letter-spacing:-.03em;background:linear-gradient(135deg,var(--pc-primary),var(--pc-accent));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;';
    h3.textContent = 'Your Perfect Match';

    /* Profile summary chips — makes results feel tailored & beautiful */
    var answers = PC_STATE.answers || {};
    var ucLabels = { apparel: 'Standard Garments', caps: 'Caps & Headwear', mugs: 'Mugs & Drinkware', 'large-format': 'Large Format', 'sports-balls': 'Sports Balls', 'high-volume': 'High-Volume DTF' };
    var volLabels = { low: 'Low Volume (<200/mo)', medium: 'Medium (200-1k/mo)', high: 'High (1k-5k/mo)', industrial: 'Industrial (5k+)' };
    var budgetLabels = { entry: 'Under $1,000', mid: '$1k–$3k', pro: '$3k–$7k', premium: '$7k–$15k', enterprise: '$15k+' };
    var autoLabels = { manual: 'Fully Manual', 'auto-open': 'Auto-Open', pneumatic: 'Pneumatic', 'rotary-auto': 'Rotary Auto' };
    var voltLabels = { '120v': '120V', '240v': '240V', '3-phase': '3-Phase' };

    var summary = createElement('div', { className: 'pc-profile-summary' });
    summary.style.cssText = 'display:flex;flex-wrap:wrap;gap:8px;justify-content:center;margin:16px 0 24px;';

    function addChip(label, icon) {
      var chip = createElement('span', { className: 'pc-profile-chip' });
      chip.style.cssText = 'display:inline-flex;align-items:center;gap:6px;padding:6px 14px;background:rgba(55,128,195,0.1);border:1px solid rgba(55,128,195,0.25);border-radius:999px;font-size:12px;font-weight:600;color:var(--pc-text);';
      if (icon) {
        var i = document.createElement('span');
        i.innerHTML = icon;
        i.style.cssText = 'font-size:13px;';
        chip.appendChild(i);
      }
      var t = document.createElement('span');
      t.textContent = label;
      chip.appendChild(t);
      summary.appendChild(chip);
    }

    if (answers.useCase) addChip(ucLabels[answers.useCase] || answers.useCase, '&#128093;');
    if (answers.volume) addChip(volLabels[answers.volume] || answers.volume, '&#128200;');
    if (answers.budget) addChip(budgetLabels[answers.budget] || answers.budget, '&#128176;');
    if (answers.automation) addChip(autoLabels[answers.automation] || answers.automation, '&#128160;');
    if (answers.voltage) addChip(voltLabels[answers.voltage] || answers.voltage, '&#9889;');

    var p = createElement('p', {});
    p.style.cssText = 'font-size:15px;color:var(--pc-text-secondary);line-height:1.7;max-width:620px;margin:0 auto 12px;';
    if (matchedCount === 0) {
      p.textContent = 'No exact matches found for your exact criteria. Here are our best alternatives that are still excellent fits.';
    } else if (matchedCount === 1) {
      p.textContent = 'We found the ideal machine perfectly matched to your shop\'s needs.';
    } else {
      p.textContent = 'We analyzed your production needs and found ' + matchedCount + ' excellent machines. Your top recommendation is highlighted below.';
    }

    /* Match count badge */
    var badge = createElement('span', {
      className: 'pc-res-badge-pulse',
      textContent: matchedCount + (matchedCount !== 1 ? ' Machines' : ' Machine') + ' • Hard Budget Cap Applied'
    });
    badge.style.cssText = 'display:inline-flex;align-items:center;gap:6px;padding:6px 18px;background:rgba(5,150,105,.1);border:1px solid rgba(5,150,105,.25);border-radius:999px;font-size:12px;font-weight:700;color:#059669;margin-bottom:8px;';

    header.appendChild(h3);
    header.appendChild(summary);
    header.appendChild(badge);
    header.appendChild(p);
    parent.appendChild(header);
  }

  function renderBestMatchCard(parent, topItem, allScored, matchedCount) {
    var prod = topItem.product;
    var card = createElement('div', { className: 'pc-best-match' });
    card.style.cssText = 'background:linear-gradient(135deg,rgba(55,128,195,.04),rgba(63,169,245,.02));border:2px solid var(--pc-primary);border-radius:16px;padding:32px;margin-bottom:32px;position:relative;';

    /* "TOP PICK" badge */
    var pickBadge = createElement('span', {
      className: 'pc-best-top-label',
      textContent: '\u2B50 TOP PICK'
    });
    pickBadge.style.cssText = 'display:flex;align-items:center;justify-content:center;gap:6px;padding:6px 16px;background:linear-gradient(135deg,var(--pc-primary),var(--pc-accent));color:#fff;font-size:11px;font-weight:800;border-radius:100px;letter-spacing:.8px;margin:0 auto 20px;width:fit-content;';
    card.appendChild(pickBadge);

    var grid = createElement('div', { className: 'pc-best-grid' });
    grid.style.cssText = 'display:grid;grid-template-columns:320px 1fr;gap:28px;';
    /* Desktop 2-col handled by media query in CSS */

    /* Image column */
    var imgWrap = createElement('div', { className: 'pc-best-img' });
    imgWrap.style.cssText = 'width:100%;aspect-ratio:4/3;border-radius:var(--pc-radius);overflow:hidden;background:var(--pc-bg);position:relative;';
    if (prod.image) {
      var img = createElement('img', { src: prod.image, alt: prod.title || 'Heat Press', loading: 'lazy' });
      img.style.cssText = 'width:100%;height:100%;object-fit:cover;';
      imgWrap.appendChild(img);
    }

    grid.appendChild(imgWrap);

    /* Details column */
    var details = createElement('div', { className: 'pc-best-dets' });
    details.style.cssText = 'display:flex;flex-direction:column;gap:12px;';

    /* Title row */
    var nameRow = createElement('div', { className: 'pc-name-row' });
    nameRow.style.cssText = 'display:flex;align-items:center;gap:12px;flex-wrap:wrap;';
    var nameEl = createElement('span', {
      className: 'pc-best-title',
      textContent: prod.title || 'Heat Press Machine'
    });
    nameEl.style.cssText = 'font-size:20px;font-weight:700;color:var(--pc-text);line-height:1.3;';
    nameRow.appendChild(nameEl);

    /* Series badge */
    var seriesBdg = createElement('span', {
      className: 'pc-series-bdg pc-series--' + (prod.series || 'VECTOR').toLowerCase(),
      textContent: prod.series || 'VECTOR'
    });
    seriesBdg.style.cssText = 'font-size:10px;font-weight:800;padding:3px 8px;border-radius:100px;letter-spacing:.5px;';
    nameRow.appendChild(seriesBdg);
    details.appendChild(nameRow);
    /* Specs grid */
    var specRows = [
      ['Platen Size', prod.platen || '\u2014'],
      ['Voltage', prod.voltage || '110V'],
      ['Power', (prod.watts && prod.watts.trim()) ? (/\w$/i.test(prod.watts.trim()) ? prod.watts.trim() : prod.watts.trim() + 'W') : '\u2014'],
      ['Output/Hr', prod.capacity ? prod.capacity + ' items' : '\u2014'],
      ['Method', prod.method || '\u2014'],
      ['Weight', prod.weight || '\u2014'],
      ['Temp Range', prod.tempRange || '\u2014']
    ];
    for (var si = 0; si < specRows.length; si++) {
      if (specRows[si][1] === '\u2014' || !specRows[si][1]) continue;
      var rowEl = createElement('div', { className: 'pc-spec-row' });
      rowEl.style.cssText = 'display:flex;justify-content:space-between;align-items:center;padding:4px 0;border-bottom:1px solid var(--pc-border);font-size:13px;';
      var lbl = createElement('span', { className: 'pc-spec-l', textContent: specRows[si][0] });
      lbl.style.cssText = 'color:var(--pc-text-secondary);';
      var val = createElement('span', { className: 'pc-spec-v', textContent: specRows[si][1] });
      val.style.cssText = 'color:var(--pc-text);font-weight:600;';
      rowEl.appendChild(lbl);
      rowEl.appendChild(val);
      details.appendChild(rowEl);
    }

    /* Price + buttons row */
    var priceRow = createElement('div', { className: 'pc-price-row' });
    priceRow.style.cssText = 'display:flex;gap:12px;align-items:center;margin-top:16px;flex-wrap:wrap;';

    var priceTag = createElement('span', {
      className: 'pc-price-tag',
      textContent: '$' + formatPrice(prod.price)
    });
    priceTag.style.cssText = 'font-size:24px;font-weight:800;color:var(--pc-primary);';
    priceRow.appendChild(priceTag);

    var viewBtn = createElement('button', { className: 'pc-btn pc-btn-outline' });
    viewBtn.textContent = 'View Product';
    viewBtn.style.cssText = 'padding:12px 24px;background:transparent;color:var(--pc-primary);border:2px solid var(--pc-primary);border-radius:8px;font-size:14px;font-weight:700;cursor:pointer;transition:0.2s;';
    viewBtn.addEventListener('click', function(e) {
      e.stopPropagation();
      window.open('/products/' + (prod.handle || prod.id), '_blank');
     });

    var cartBtn = createElement('button', { className: 'pc-btn pc-btn-primary' });
    cartBtn.textContent = 'Add to Cart';
    cartBtn.style.cssText = 'padding:12px 24px;background:var(--pc-primary);color:#fff;border:0;border-radius:8px;font-size:14px;font-weight:700;cursor:pointer;transition:0.2s;';
    cartBtn.addEventListener('click', function(e) {
      e.stopPropagation();
      addToCart(prod);
     });

    priceRow.appendChild(viewBtn);
    priceRow.appendChild(cartBtn);
    details.appendChild(priceRow);

    /* Feature badges */
    var badgeItems = [];
    var methodLower = ((prod && prod.method) || '').toLowerCase();
    if (methodLower.indexOf('auto') !== -1 || methodLower.indexOf('spring') !== -1) {
      badgeItems.push({ cls: 'pc-bdg-auto', text: 'Auto-Open' });
    }
    var pArea = calcPlatenArea(prod.platenW, prod.platenH);
    if (pArea >= 936) {
      badgeItems.push({ cls: 'pc-bdg-lg', text: 'Large Format' });
    }
    if (prod.useCase === 'caps' || prod.useCase === 'mugs') {
      badgeItems.push({ cls: 'pc-bdg-rot', text: 'Interchangeable Platens' });
    }
    if (badgeItems.length > 0) {
      var bdgRow = createElement('div', { className: 'pc-bdg-row' });
      bdgRow.style.cssText = 'display:flex;gap:8px;flex-wrap:wrap;';
      for (var bi = 0; bi < badgeItems.length; bi++) {
        var bdg = createElement('span', {
          className: 'pc-bdg ' + badgeItems[bi].cls,
          textContent: badgeItems[bi].text
        });
        bdg.style.cssText = 'font-size:10px;font-weight:700;padding:4px 10px;border-radius:100px;letter-spacing:.3px;';
        bdgRow.appendChild(bdg);
      }
      details.appendChild(bdgRow);
    }

    grid.appendChild(details);
    card.appendChild(grid);

    parent.appendChild(card);
  }

/* =========================================================================
   MATCH REASON — generates a short human-readable reason per product.
   ========================================================================= */
  function getMatchReason(product, answers) {
    var reasons = [];
    /* Use case match */
    var ucLabels = {
      'apparel': 'Standard garments',
      'caps': 'Caps & headwear',
      'mugs': 'Mugs & drinkware',
      'large-format': 'Large format printing',
      'sports-balls': 'Sports balls & specialty',
      'high-volume': 'High-volume DTF production'
    };
    if (product.useCase === answers.useCase) {
      reasons.push('Perfect for ' + (ucLabels[answers.useCase] || answers.useCase));
    } else if (product.useCase === 'apparel' && ['mugs', 'caps', 'sports-balls', 'large-format'].indexOf(answers.useCase) !== -1) {
      reasons.push('Versatile general-purpose press');
    }
    /* Budget fit */
    if (answers.budget) {
      var range = BUDGET_RANGES[answers.budget];
      var price = product.price || 0;
      if (price <= range[1]) {
        reasons.push('Within budget');
      } else {
        var overPct = ((price - range[1]) / range[1]) * 100;
        reasons.push('Slightly above budget (' + Math.round(overPct) + '% over)');
      }
    }
    /* Volume fit */
    if (answers.volume && product.capacity) {
      var volMap = { low: 'entry', medium: 'medium', high: 'pro', industrial: 'industrial' };
      var targetVol = volMap[answers.volume];
      if (product.capacity === targetVol) {
        reasons.push('Right fit for your output');
      }
    }
    /* Automation match */
    if (answers.automation) {
      var method = ((product && product.method) || '').toLowerCase();
      if (answers.automation === 'manual' && (method.indexOf('manual') !== -1 || method.indexOf('hand') !== -1)) {
        reasons.push('Manual operation');
      }
      if (answers.automation === 'auto-open' && (method.indexOf('auto') !== -1 || method.indexOf('electromagnetic') !== -1 || method.indexOf('spring') !== -1)) {
        reasons.push('Auto-open feature');
      }
      if (answers.automation === 'pneumatic' && (method.indexOf('pneumatic') !== -1 || method.indexOf('air-powered') !== -1)) {
        reasons.push('Pneumatic assist');
      }
      if (answers.automation === 'rotary-auto' && (method.indexOf('rotary') !== -1 || method.indexOf('automatic') !== -1)) {
        reasons.push('Fully automatic');
      }
    }
    /* Voltage */
    if (answers.voltage) {
      var prodNorm = getVoltageNormalized(product);
      var vMap = { '120v': '120V', '240v': '240V', '3-phase': '3-Phase' };
      if (prodNorm === '120v' && answers.voltage === '120v') reasons.push('120V compatible');
      if (prodNorm === '240v' && answers.voltage === '240v') reasons.push('240V compatible');
      if (answers.voltage === '3-phase') reasons.push('All voltage compatible');
    }
    if (reasons.length === 0) reasons.push('Solid all-around choice');
    return reasons.slice(0, 2).join(', ');
  }

  /* =========================================================================
     MATCHED TABLE — all scored products ranked by match, replacing alternatives grid.
     createElement-based, responsive card-style rows on mobile, horizontal on desktop.
     ========================================================================= */
  function renderMatchedTable(parent, matchedList) {
    var section = createElement('div', { className: 'pc-alts-section' });
    section.style.cssText = 'margin-bottom:32px;animation:pcFadeIn 0.6s ease-out 0.3s both;';

    var title = createElement('h4', {
      className: 'pc-alts-title',
      textContent: 'All Matching Machines'
    });
    title.style.cssText = 'font-size:clamp(22px,4vw,28px);font-weight:700;color:var(--pc-text);text-align:center;margin-bottom:4px;letter-spacing:-0.02em;';
    section.appendChild(title);

    var count = (matchedList && matchedList.length) || 0;
    var subtitle = createElement('p', {
      className: 'pc-alts-subtitle',
      textContent: count + ' machines matched your criteria • Ranked by fit'
    });
    subtitle.style.cssText = 'font-size:13px;color:var(--pc-text-secondary);text-align:center;margin-bottom:16px;line-height:1.4;';
    section.appendChild(subtitle);

    /* Use the already-filtered matched list (passed from renderResultsPage) */
    var ranked = (matchedList || []).slice().sort(function(a, b) { return b.score - a.score; });

    if (ranked.length === 0) {
      var empty = createElement('div', { className: 'pc-mt-empty' });
      empty.style.cssText = 'text-align:center;padding:32px 20px;color:var(--pc-text-secondary);font-size:14px;';
      empty.textContent = 'No matching machines found. Try adjusting your answers.';
      section.appendChild(empty);
      parent.appendChild(section);
      return;
    }

    var maxScore = ranked[0].score;

    /* Proper semantic table */
    var table = createElement('table', { className: 'pc-mt-table' });
    table.style.cssText = 'width:100%;border:1px solid var(--pc-border);border-radius:var(--pc-radius);overflow:hidden;background:var(--pc-surface);border-collapse:collapse;';

    /* THEAD */
    var thead = createElement('thead', {});
    var headerRow = createElement('tr', {});
    headerRow.style.cssText = 'background:var(--pc-surface);';

    var headers = [
      { text: 'Rank', style: 'width:56px;text-align:center;' },
      { text: 'Product', style: 'min-width:220px;' },
      { text: 'Price', style: 'width:90px;text-align:right;' },
      { text: 'Match', style: 'width:80px;text-align:center;' },
      { text: 'Why it fits', style: '' },
      { text: '', style: 'width:140px;' } // Actions
    ];

    headers.forEach(function(h) {
      var th = createElement('th', { textContent: h.text });
      th.style.cssText = 'padding:10px 12px;font-size:11px;font-weight:700;text-transform:uppercase;color:var(--pc-text-secondary);letter-spacing:0.5px;text-align:left;border-bottom:2px solid var(--pc-primary);' + h.style;
      headerRow.appendChild(th);
    });
    thead.appendChild(headerRow);
    table.appendChild(thead);

    /* TBODY */
    var tbody = createElement('tbody', {});

    for (var i = 0; i < ranked.length; i++) {
      var item = ranked[i];
      var prod = item.product;
      var pct = maxScore > 0 ? Math.round((item.score / maxScore) * 100) : 0;
      var rank = i + 1;
      var isTop = rank <= 3;
      var reason = getMatchReason(prod, PC_STATE.answers);

      var tr = createElement('tr', {});
      tr.style.cssText = 'border-bottom:1px solid var(--pc-border);transition:background .2s;cursor:pointer;';
      if (i === ranked.length - 1) tr.style.borderBottom = 'none';

      /* Rank */
      var rankTd = createElement('td', {});
      rankTd.style.cssText = 'padding:10px 12px;text-align:center;vertical-align:middle;';
      var rankBadge = createElement('div', { className: 'pc-mt-rank' + (isTop ? ' pc-mt-rank--top' : '') });
      rankBadge.style.cssText = 'width:28px;height:28px;border-radius:50%;background:' + (isTop ? 'linear-gradient(135deg,var(--pc-primary),var(--pc-accent))' : 'var(--pc-bg)') + ';color:' + (isTop ? '#fff' : 'var(--pc-text-secondary)') + ';display:inline-flex;align-items:center;justify-content:center;font-size:12px;font-weight:800;font-family:Inter,monospace;border:1px solid var(--pc-border);';
      rankBadge.textContent = rank;
      rankTd.appendChild(rankBadge);
      tr.appendChild(rankTd);

      /* Product (75x75 image + title + series) */
      var prodTd = createElement('td', {});
      prodTd.style.cssText = 'padding:10px 12px;vertical-align:middle;';

      var prodWrap = createElement('div', {});
      prodWrap.style.cssText = 'display:flex;align-items:center;gap:12px;';

      var thumb = createElement('div', { className: 'pc-mt-thumb' });
      thumb.style.cssText = 'width:75px;height:75px;border-radius:8px;overflow:hidden;flex-shrink:0;background:var(--pc-bg);border:1px solid var(--pc-border);';
      if (prod.image) {
        var img = createElement('img', { src: prod.image, alt: prod.title || '', loading: 'lazy' });
        img.style.cssText = 'width:100%;height:100%;object-fit:cover;display:block;';
        thumb.appendChild(img);
      }
      prodWrap.appendChild(thumb);

      var info = createElement('div', {});
      info.style.cssText = 'min-width:0;';

      var nameEl = document.createElement('div');
      nameEl.style.cssText = 'font-size:14px;font-weight:700;color:var(--pc-text);line-height:1.25;margin-bottom:2px;';
      nameEl.textContent = prod.title;
      info.appendChild(nameEl);

      var seriesEl = createElement('span', {
        className: 'pc-mt-series pc-series--' + (prod.series || 'VECTOR').toLowerCase(),
        textContent: prod.series || 'VECTOR'
      });
      seriesEl.style.cssText = 'font-size:10px;font-weight:700;color:var(--pc-primary);text-transform:uppercase;letter-spacing:.5px;';
      info.appendChild(seriesEl);

      prodWrap.appendChild(info);
      prodTd.appendChild(prodWrap);
      tr.appendChild(prodTd);

      /* Price */
      var priceTd = createElement('td', {});
      priceTd.style.cssText = 'padding:10px 12px;text-align:right;vertical-align:middle;white-space:nowrap;';
      var priceEl = createElement('span', {
        className: 'pc-mt-price',
        textContent: '$' + formatPrice(prod.price)
      });
      priceEl.style.cssText = 'font-size:15px;font-weight:800;color:var(--pc-primary);';
      priceTd.appendChild(priceEl);
      tr.appendChild(priceTd);

      /* Match % */
      var matchTd = createElement('td', {});
      matchTd.style.cssText = 'padding:10px 12px;text-align:center;vertical-align:middle;';
      var matchWrap = createElement('div', {});
      matchWrap.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:2px;';

      var pctEl = createElement('span', {
        className: 'pc-mt-pct',
        textContent: pct + '%'
      });
      pctEl.style.cssText = 'font-size:18px;font-weight:900;background:linear-gradient(135deg,var(--pc-primary),var(--pc-accent));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;line-height:1;font-family:Inter,monospace;';
      matchWrap.appendChild(pctEl);

      var pctLabel = createElement('span', {
        className: 'pc-mt-pct-label',
        textContent: 'MATCH'
      });
      pctLabel.style.cssText = 'font-size:9px;font-weight:700;color:var(--pc-text-secondary);text-transform:uppercase;letter-spacing:.5px;';
      matchWrap.appendChild(pctLabel);
      matchTd.appendChild(matchWrap);
      tr.appendChild(matchTd);

      /* Why it fits (reason) */
      var reasonTd = createElement('td', {});
      reasonTd.style.cssText = 'padding:10px 12px;vertical-align:middle;';
      var reasonEl = createElement('span', {
        className: 'pc-mt-reason',
        textContent: reason
      });
      reasonEl.style.cssText = 'font-size:12px;color:var(--pc-text-secondary);line-height:1.4;';
      reasonTd.appendChild(reasonEl);
      tr.appendChild(reasonTd);

      /* Actions */
      var actionsTd = createElement('td', {});
      actionsTd.style.cssText = 'padding:10px 12px;vertical-align:middle;white-space:nowrap;';
      var actionsWrap = createElement('div', {});
      actionsWrap.style.cssText = 'display:flex;gap:6px;justify-content:flex-end;';

      var viewBtn = createElement('button', { className: 'pc-mt-btn pc-mt-btn-view' });
      viewBtn.textContent = 'View';
      viewBtn.style.cssText = 'font-family:Inter,sans-serif;font-size:11px;font-weight:700;padding:6px 12px;border-radius:6px;cursor:pointer;transition:all .2s;background:transparent;color:var(--pc-primary);border:1.5px solid var(--pc-primary);';
      (function(p) {
        viewBtn.addEventListener('click', function(e) {
          e.stopPropagation();
          window.open('/products/' + (p.handle || p.id), '_blank');
        });
      })(prod);
      actionsWrap.appendChild(viewBtn);

      var cartBtn = createElement('button', { className: 'pc-mt-btn pc-mt-btn-cart' });
      cartBtn.textContent = 'Add to Cart';
      cartBtn.style.cssText = 'font-family:Inter,sans-serif;font-size:11px;font-weight:700;padding:6px 12px;border-radius:6px;cursor:pointer;transition:all .2s;background:var(--pc-primary);color:#fff;border:1.5px solid var(--pc-primary);';
      (function(p) {
        cartBtn.addEventListener('click', function(e) {
          e.stopPropagation();
          addToCart(p);
        });
      })(prod);
      actionsWrap.appendChild(cartBtn);

      actionsTd.appendChild(actionsWrap);
      tr.appendChild(actionsTd);

      /* Row click → modal */
      (function(p) {
        tr.addEventListener('click', function() {
          openProductModal(p);
        });
      })(prod);

      tbody.appendChild(tr);
    }

    table.appendChild(tbody);
    section.appendChild(table);
    parent.appendChild(section);
  }

  function maxScoreForProduct(product, allScored) {
    var max = 0;
    if (!allScored || !allScored.length) return 1;
    for (var i = 0; i < allScored.length; i++) {
      if (product && allScored[i].product && allScored[i].product.id === product.id) {
        max = Math.max(max, allScored[i].score);
      }
    }
    return max > 0 ? max : 1;
  }

/* =========================================================================
   SERIES COMPARISON — brand tier overview cards.
   Auto-derives from product data: unique series \u2192 price ranges + counts.
    ========================================================================= */
  function renderZeroResultsSupport(parent) {
    var root = PC_DOM.root;
    var phone = (root && root.getAttribute('data-pc-support-phone')) || '(555) 123-4567';
    var linkUrl = (root && root.getAttribute('data-pc-support-link')) || '';
    var linkText = (root && root.getAttribute('data-pc-support-link-text')) || 'Contact Support';

    var card = createElement('div', { className: 'pc-zero-support' });
    card.style.cssText = 'margin:24px auto 40px;max-width:620px;background:var(--pc-surface);border:1px solid var(--pc-border);border-radius:var(--pc-radius);padding:28px 32px;text-align:center;box-shadow:0 4px 12px rgba(0,0,0,0.06);';

    var icon = createElement('div', { innerHTML: '&#128172;' });
    icon.style.cssText = 'font-size:42px;margin-bottom:12px;';
    card.appendChild(icon);

    var title = createElement('h3', {
      textContent: 'No machines match your exact criteria'
    });
    title.style.cssText = 'font-size:20px;font-weight:700;color:var(--pc-text);margin-bottom:8px;';
    card.appendChild(title);

    var msg = createElement('p', {
      textContent: 'Your combination of budget, power, and application has no exact match in our current lineup. One of our sales agents can help you find the right solution - call us and we will match you personally.'
    });
    msg.style.cssText = 'font-size:14px;color:var(--pc-text-secondary);max-width:480px;margin:0 auto 20px;line-height:1.5;';
    card.appendChild(msg);

    var phoneLine = createElement('div', {
      innerHTML: '<strong style="color:var(--pc-text);">Call a sales agent:</strong> <a href="tel:' + phone.replace(/[^0-9+]/g, '') + '" style="color:var(--pc-primary);font-weight:600;text-decoration:none;margin-left:4px;">' + phone + '</a>'
    });
    phoneLine.style.cssText = 'margin-bottom:20px;font-size:15px;';
    card.appendChild(phoneLine);

    if (linkUrl) {
      var btn = createElement('a', {
        href: linkUrl,
        className: 'pc-btn pc-btn-primary',
        textContent: linkText
      });
      btn.style.cssText = 'display:inline-flex;align-items:center;justify-content:center;gap:8px;padding:12px 28px;border-radius:9999px;font-weight:700;text-decoration:none;background:var(--pc-primary);color:#fff;';
      card.appendChild(btn);
    }

    parent.appendChild(card);
  }

  function renderSeriesComparison(parent, products) {
    var section = createElement('div', { className: 'pc-comp-section' });
    section.style.cssText = 'margin-bottom:40px;';

    var h4 = createElement('h4', {
      className: '',
      textContent: 'Which Series Is Right For You'
    });
    h4.style.cssText = 'font-size:24px;font-weight:700;color:var(--pc-text);text-align:center;margin-bottom:12px;';
    section.appendChild(h4);

    /* Educational intro about the four-series growth path */
    var intro = createElement('div', {});
    intro.style.cssText = 'max-width:720px;margin:0 auto 28px;text-align:center;';
    intro.innerHTML = 
      '<p style="font-size:14.5px;color:var(--pc-text-secondary);line-height:1.65;margin-bottom:16px;">' +
      'We created this four-series lineup to help customers quickly understand which Microtec solution fits their stage of growth.' +
      '</p>' +
      '<p style="font-size:13.5px;color:var(--pc-text-secondary);line-height:1.6;margin-bottom:0;">' +
      '<strong>Vector</strong> is our entry-level series for home users, startups, and small businesses. ' +
      '<strong>TITAN</strong> is our commercial series focused on popular sizes (16×20 / 16×24) with dual-station options for better speed. ' +
      '<strong>ATLAS</strong> is our large-format series (24–48") for oversized graphics and demanding production. ' +
      '<strong>APEX</strong> is our automation series — carousels, auto feeding, and high-volume systems to help you scale.' +
      '</p>' +
      '<p style="font-size:13px;color:var(--pc-text);margin-top:14px;font-weight:600;">' +
      'Start with Vector → Grow with TITAN → Expand with ATLAS → Automate &amp; scale with APEX.' +
      '</p>';
    section.appendChild(intro);

    /* Derive unique series */
    var seriesSet = {};
    for (var i = 0; i < products.length; i++) {
      if (products[i].series) seriesSet[products[i].series] = true;
    }
    var seriesList = Object.keys(seriesSet).sort();

    if (seriesList.length === 0) return;

    var grid = createElement('div', {});
    grid.style.cssText = 'display:flex;gap:12px;flex-wrap:nowrap;align-items:stretch;overflow-x:auto;';
    /* CSS media queries handle responsive columns */

    for (var si = 0; si < seriesList.length; si++) {
      var series = seriesList[si];
      var seriesProds = products.filter(function(p) { return p.series === series; });
      var card = createElement('div', {});
      card.style.cssText = 'background:var(--pc-surface);border:1px solid var(--pc-border);border-radius:14px;padding:20px;transition:all .3s ease;flex:0 0 calc(25% - 9px);min-width:200px;display:flex;flex-direction:column;justify-content:space-between;';

      /* Series badge */
      var badge = createElement('span', {
        className: 'pc-series-bdg pc-series--' + series.toLowerCase(),
        textContent: series
      });
      badge.style.cssText = 'font-size:10px;font-weight:800;padding:3px 8px;border-radius:100px;letter-spacing:.5px;display:block;margin-bottom:12px;';
      card.appendChild(badge);

      /* Description */
      var desc = createElement('p', { className: '' });
      desc.style.cssText = 'font-size:13px;color:var(--pc-text-secondary);line-height:1.5;margin:0;';
      desc.innerHTML = getSeriesDescription(series);
      card.appendChild(desc);

      /* Price range */
      if (seriesProds.length > 0) {
        var prices = [];
        for (var pi = 0; pi < seriesProds.length; pi++) prices.push(seriesProds[pi].price || 0);
        var minP = Math.min.apply(null, prices);
        var maxP = Math.max.apply(null, prices);
        var priceEl = createElement('p', {
          className: '',
          textContent: 'From $' + formatPrice(minP) + ' \u2014 $' + formatPrice(maxP)
        });
        priceEl.style.cssText = 'font-size:13px;font-weight:600;color:var(--pc-text);margin-top:auto;margin-bottom:4px;';
        card.appendChild(priceEl);
      }

      /* Count */
      var countEl = createElement('p', {
        className: '',
        textContent: seriesProds.length + (seriesProds.length !== 1 ? ' machines' : ' machine')
      });
      countEl.style.cssText = 'font-size:12px;color:var(--pc-text-secondary);margin-top:4px;';
      card.appendChild(countEl);

      grid.appendChild(card);
    }

    section.appendChild(grid);
    parent.appendChild(section);
  }

  function getSeriesDescription(series) {
    var descriptions = {
      'VECTOR': '<strong>Entry-Level Reliable</strong>\nProven performance for small businesses getting started. Simple manual operation, solid build quality, best value.',
      'TITAN': '<strong>Professional Grade</strong>\nBuilt for growing operations. Higher output, faster cycling, optional auto-open for consistency.',
      'ATLAS': '<strong>Industrial Scale</strong>\nLarge platens, high power, maximum throughput. Designed for established production facilities.',
      'APEX': '<strong>Premium Automated</strong>\nTurnkey solutions with advanced features. Dual stations, rotary platens, laser calibration for precision.'
    };
    return descriptions[series] || '<strong>Custom Series</strong>\nSpecialized equipment for unique applications.';
  }

/* =========================================================================
   COMPARISON TABLE — all products, sortable/filterable by series.
   createElement-based table with thumbnail column + series badges.
    ========================================================================= */
  function renderComparisonTable(parent, products) {
    if (!products || products.length === 0) return;
    var section = createElement('div', { className: 'pc-comp-section' });
    section.style.cssText = 'margin-bottom:40px;';

    var h4 = createElement('h4', {
      className: '',
      textContent: PC_DOM.root ? (PC_DOM.root.getAttribute('data-pc-comp-heading') || 'Compare All Machines') : 'Compare All Machines'
    });
    h4.style.cssText = 'font-size:24px;font-weight:700;color:var(--pc-text);text-align:center;margin-bottom:4px;';
    section.appendChild(h4);

    var subText = 'Full lineup • VECTOR • TITAN • ATLAS • APEX';
    if (PC_STATE.answers && (PC_STATE.answers.budget || PC_STATE.answers.voltage)) {
      subText = 'Machines within your budget & power • VECTOR • TITAN • ATLAS • APEX';
    }
    var sub = createElement('p', {
      textContent: subText
    });
    sub.style.cssText = 'font-size:13px;color:var(--pc-text-secondary);text-align:center;margin-bottom:20px;';
    section.appendChild(sub);

    /* Series filter buttons */
    var seriesSet = {};
    for (var i = 0; i < products.length; i++) {
      if (products[i].series) seriesSet[products[i].series] = true;
    }
    var seriesKeys = Object.keys(seriesSet).sort();

    /* Filter row */
    var filterRow = createElement('div', {});
    filterRow.style.cssText = 'display:flex;justify-content:center;gap:8px;margin-bottom:16px;flex-wrap:wrap;';

    var allBtn = createElement('button', {
      className: 'pc-filter-btn pc-filter-btn-sel',
      textContent: 'All'
    });
    allBtn.style.cssText = 'background:var(--pc-primary);border-color:var(--pc-primary);color:#fff;';
    (function(btn) {
      btn.addEventListener('click', function() {
        var filters = filterRow.querySelectorAll('.pc-filter-btn');
        for (var f = 0; f < filters.length; f++) {
          filters[f].style.background = 'var(--pc-surface)';
          filters[f].style.borderColor = 'var(--pc-border)';
          filters[f].style.color = 'var(--pc-text-secondary)';
        }
        btn.style.background = 'var(--pc-primary)';
        btn.style.borderColor = 'var(--pc-primary)';
        btn.style.color = '#fff';

        // Reset all series filter buttons to inactive state
        var seriesFilters = filterRow.querySelectorAll('.pc-filter-btn:not(.pc-filter-btn-sel)');
        for (var sf = 0; sf < seriesFilters.length; sf++) {
          seriesFilters[sf].style.background = 'rgba(0,0,0,0.03)';
          seriesFilters[sf].style.borderColor = 'var(--pc-border)';
          seriesFilters[sf].style.color = 'var(--pc-text-secondary)';
        }

        var rows = section.querySelectorAll('.pc-table-tbody tr');
        for (var r = 0; r < rows.length; r++) rows[r].style.display = '';
      });
    })(allBtn);
    filterRow.appendChild(allBtn);

    /* Series-specific filter buttons — color coded to match table badges */
    for (var sk = 0; sk < seriesKeys.length; sk++) {
      (function(series, btn) {
        var seriesLower = series.toLowerCase();
        btn.className = 'pc-filter-btn pc-series--' + seriesLower;

        // Default (inactive) style — matches the badge look
        btn.style.cssText = 'background:rgba(0,0,0,0.03);border:1px solid var(--pc-border);color:var(--pc-text-secondary);';

        btn.addEventListener('click', function() {
          var filters = filterRow.querySelectorAll('.pc-filter-btn');
          for (var f = 0; f < filters.length; f++) {
            // Reset all to inactive
            filters[f].style.background = 'rgba(0,0,0,0.03)';
            filters[f].style.borderColor = 'var(--pc-border)';
            filters[f].style.color = 'var(--pc-text-secondary)';
          }
          // Active state — solid series color
          if (seriesLower === 'vector') {
            btn.style.background = '#3780C3';
            btn.style.borderColor = '#3780C3';
            btn.style.color = '#fff';
          } else if (seriesLower === 'titan') {
            btn.style.background = 'var(--pc-accent)';
            btn.style.borderColor = 'var(--pc-accent)';
            btn.style.color = '#fff';
          } else if (seriesLower === 'atlas') {
            btn.style.background = '#10b981';
            btn.style.borderColor = '#10b981';
            btn.style.color = '#fff';
          } else if (seriesLower === 'apex') {
            btn.style.background = '#dc2626';
            btn.style.borderColor = '#dc2626';
            btn.style.color = '#fff';
          } else {
            btn.style.background = 'var(--pc-primary)';
            btn.style.borderColor = 'var(--pc-primary)';
            btn.style.color = '#fff';
          }

          var rows = section.querySelectorAll('.pc-table-tbody tr');
          for (var r = 0; r < rows.length; r++) {
            rows[r].style.display = (rows[r].getAttribute('data-series') === series) ? '' : 'none';
          }
        });
        filterRow.appendChild(btn);
      })(seriesKeys[sk], createElement('button', { textContent: seriesKeys[sk] }));
    }

    section.appendChild(filterRow);

    /* Table wrapper */
    var tableWrap = createElement('div', {});
    tableWrap.style.cssText = 'overflow-x:auto;border:1px solid var(--pc-border);border-radius:var(--pc-radius);background:var(--pc-surface);';

    var table = createElement('table', {});
    table.style.cssText = 'width:100%;border-collapse:collapse;min-width:700px;';

    /* Thead */
    var thead = createElement('thead', {});
    var headerRow = createElement('tr', {});
    ['Product', 'Series', 'Price', 'Capacity', 'Platen', 'Watts', 'Voltage', 'Temp Range', 'Weight'].forEach(function(h) {
      var th = createElement('th', { textContent: h });
      th.style.cssText = 'background:var(--pc-surface);padding:12px 14px;font-size:10px;font-weight:700;text-transform:uppercase;color:var(--pc-text-secondary);text-align:left;border-bottom:2px solid var(--pc-primary);';
      headerRow.appendChild(th);
    });
    thead.appendChild(headerRow);
    table.appendChild(thead);

    /* Tbody */
    var tbody = createElement('tbody', { className: 'pc-table-tbody' });
    /* Sort by price ascending */
    var sortedProducts = products.slice().sort(function(a, b) { return (a.price || 0) - (b.price || 0); });

    for (var pi = 0; pi < sortedProducts.length; pi++) {
      var prod = sortedProducts[pi];
      var tr = createElement('tr', {});
      tr.setAttribute('data-series', prod.series || '');
      tr.style.cssText = 'border-bottom:1px solid var(--pc-border);transition:background .2s;cursor:pointer;';

      /* Product cell with thumbnail */
      (function(pd) {
        var td = createElement('td', {});
        td.style.cssText = 'padding:12px 14px;font-size:13px;color:var(--pc-text);vertical-align:middle;';
        var itemWrap = createElement('div', { className: 'pc-comp-item' });
        itemWrap.style.cssText = 'display:flex;align-items:center;gap:10px;';

        var thumb = createElement('div', { className: 'pc-comp-thumb' });
        thumb.style.cssText = 'width:36px;height:36px;border-radius:6px;overflow:hidden;flex-shrink:0;background:var(--pc-bg);';
        if (pd.image) {
          var tImg = createElement('img', { src: pd.image, alt: pd.title || '', loading: 'lazy' });
          tImg.style.cssText = 'width:100%;height:100%;object-fit:cover;';
          thumb.appendChild(tImg);
        }
        itemWrap.appendChild(thumb);

        var nameSpan = document.createElement('span');
        nameSpan.textContent = pd.title || '';
        nameSpan.style.cssText = 'font-size:13px;font-weight:600;color:var(--pc-text);';
        itemWrap.appendChild(nameSpan);
        td.appendChild(itemWrap);
        tr.appendChild(td);
      })(prod);

      /* Series badge cell */
      (function(pd) {
        var td = createElement('td', {});
        td.style.cssText = 'padding:12px 14px;font-size:13px;color:var(--pc-text);vertical-align:middle;';
        var bdg = createElement('span', {
          className: 'pc-series-bdg pc-series--' + (pd.series || 'VECTOR').toLowerCase(),
          textContent: pd.series || 'VECTOR'
        });
        bdg.style.cssText = 'font-size:10px;font-weight:800;padding:3px 8px;border-radius:100px;letter-spacing:.5px;';
        td.appendChild(bdg);
        tr.appendChild(td);
      })(prod);

      /* Price */
      (function(pd) {
        var td = createElement('td', {});
        td.style.cssText = 'padding:12px 14px;font-size:13px;color:var(--pc-text);vertical-align:middle;';
        td.textContent = '$' + formatPrice(pd.price);
        tr.appendChild(td);
      })(prod);

      /* Capacity */
      (function(pd) {
        var td = createElement('td', {});
        td.style.cssText = 'padding:12px 14px;font-size:13px;color:var(--pc-text);vertical-align:middle;';
        td.textContent = pd.capacity || '\u2014';
        tr.appendChild(td);
      })(prod);

      /* Platen — show full dimensions, rounded */
      (function(pd) {
        var td = createElement('td', {});
        td.style.cssText = 'padding:12px 14px;font-size:13px;color:var(--pc-text);vertical-align:middle;white-space:nowrap;';
        var w = pd.platenW ? Math.round(parseFloat(pd.platenW)) : null;
        var h = pd.platenH ? Math.round(parseFloat(pd.platenH)) : null;
        var platenText = (w && h) ? (w + ' × ' + h + '"') : (pd.platen || '\u2014');
        td.textContent = platenText;
        tr.appendChild(td);
      })(prod);

      /* Watts */
      (function(pd) {
        var td = createElement('td', {});
        td.style.cssText = 'padding:12px 14px;font-size:13px;color:var(--pc-text);vertical-align:middle;';
        var wText = pd.watts ? ((/\w$/i.test(pd.watts.trim()) ? pd.watts.trim() : pd.watts.trim() + 'W') || '\u2014') : '\u2014';
        td.textContent = wText;
        tr.appendChild(td);
      })(prod);

      /* Voltage */
      (function(pd) {
        var td = createElement('td', {});
        td.style.cssText = 'padding:12px 14px;font-size:13px;color:var(--pc-text);vertical-align:middle;';
        td.textContent = pd.voltage || '\u2014';
        tr.appendChild(td);
      })(prod);

      /* Temp Range */
      (function(pd) {
        var td = createElement('td', {});
        td.style.cssText = 'padding:12px 14px;font-size:13px;color:var(--pc-text);vertical-align:middle;';
        td.textContent = pd.tempRange || '\u2014';
        tr.appendChild(td);
      })(prod);

      /* Weight */
      (function(pd) {
        var td = createElement('td', {});
        td.style.cssText = 'padding:12px 14px;font-size:13px;color:var(--pc-text);vertical-align:middle;';
        td.textContent = pd.weight || '\u2014';
        tr.appendChild(td);
      })(prod);

      /* Row click opens product modal */
      (function(p) {
        tr.addEventListener('click', function() { openProductModal(p); });
      })(prod);

      tbody.appendChild(tr);
    }

    table.appendChild(tbody);
    tableWrap.appendChild(table);
    section.appendChild(tableWrap);
    parent.appendChild(section);
  }

/* =========================================================================
   CTA ROW — bridge links to full product view.
    ========================================================================= */
  function renderCTARow(parent, root) {
    var ctaWrap = createElement('div', {});
    ctaWrap.style.cssText = 'display:flex;gap:12px;justify-content:center;flex-wrap:wrap;margin-top:28px;margin-bottom:8px;';

    /* Shop button */
    var colHandle = root.getAttribute('data-collection-handle');
    var shopBtn = createElement('a', {
      className: 'pc-cta-btn pc-cta-primary',
      textContent: 'Shop All Heat Press Machines \u2192'
    });
    var domain = root.getAttribute('data-pc-domain') || '{{ shop.primary_domain }}';
    shopBtn.href = colHandle ? domain + '/collections/' + colHandle : domain + '/collections/all';
    shopBtn.target = '_self';
    shopBtn.style.cssText = 'display:inline-block;padding:14px 28px;border-radius:12px;font-size:14px;font-weight:700;cursor:pointer;text-decoration:none !important;transition:all .3s ease;';
    ctaWrap.appendChild(shopBtn);

    parent.appendChild(ctaWrap);
  }

  function renderRestartSection(parent) {
    var wrap = createElement('div', {});
    wrap.style.cssText = 'text-align:center;margin-bottom:40px;';

    var btn = createElement('button', {
      className: 'pc-btn-outline',
      textContent: '\u21BA Start Over'
    });
    btn.style.cssText = 'background:transparent;color:var(--pc-text-secondary);border:1.5px solid var(--pc-border);font-family:\'Inter\',sans-serif;font-size:14px;font-weight:600;padding:12px 24px;border-radius:10px;cursor:pointer;transition:all .2s;display:inline-flex;align-items:center;gap:8px;';
    btn.addEventListener('click', function() { window.PC_INIT(); });

    wrap.appendChild(btn);
    parent.appendChild(wrap);
  }

/* =========================================================================
   CART HELPER — adds a product to cart via Shopify cart API.
   ========================================================================== */
  function addToCart(prod) {
    if (!prod || !prod.variantId) {
      alert('Product variant not found. Please use "View Product" to add to cart.');
      return;
      }
      /* Use themeCore CartApi if available (Ultra theme), fallback to fetch */
    if (window.themeCore && window.themeCore.CartApi && window.themeCore.CartApi.actions) {
      window.themeCore.CartApi.makeRequest(
        window.themeCore.CartApi.actions.ADD_TO_CART,
         { id: prod.variantId, quantity: 1 }
        ).then(function() {
          /* Refresh cart UI */
          window.themeCore.CartApi.makeRequest(window.themeCore.CartApi.actions.GET_CART);
        }).catch(function(err) {
        alert(err && err.description ? err.description : 'Could not add to cart. Please try again.');
        });
      } else {
        /* Fallback: Shopify AJAX API */
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: prod.variantId, quantity: 1 }] })
        }).catch(function() {
        alert('Could not add to cart. Please use "View Product" instead.');
        });
      }
    }



/* =========================================================================
   PRODUCT MODAL — enhanced pop-up with specs + CTA.
    ========================================================================= */
  function openProductModal(prod) {
    var root = getRoot();
    if (!root) return;

    var overlay = createElement('div', {});
    overlay.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,.85);backdrop-filter:blur(4px);z-index:10000;display:flex;align-items:center;justify-content:center;padding:24px;animation:pcFadeIn .3s ease;';

    var modal = createElement('div', {});
    modal.style.cssText = 'background:#2a2a2a;border:1px solid var(--pc-border);border-radius:16px;max-width:700px;width:100%;max-height:90vh;overflow-y:auto;animation:pcSlideUp .4s ease;';

    var closeBtn = createElement('button', { className: 'pc-modal-x' });
    closeBtn.textContent = '\u00D7';
    closeBtn.style.cssText = 'position:absolute;top:12px;right:12px;width:32px;height:32px;background:var(--pc-surface);border:1px solid var(--pc-border);border-radius:50%;color:var(--pc-text);font-size:20px;cursor:pointer;display:flex;align-items:center;justify-content:center;z-index:5;transition:all .2s;';
    closeBtn.addEventListener('click', function() { overlay.remove(); });
    modal.appendChild(closeBtn);
     /* Image - smaller on modal */
    if (prod.image) {
      var imgWrap = createElement('div', {});
      imgWrap.style.cssText = 'width:100%;max-height:220px;overflow:hidden;border-radius:16px 16px 0 0;background:var(--pc-bg);position:relative;';
      var img = createElement('img', { src: prod.image, alt: prod.title || 'Heat Press', loading: 'lazy' });
      img.style.cssText = 'width:100%;height:100%;object-fit:cover;';
      imgWrap.appendChild(img);
      modal.appendChild(imgWrap);
     }

     /* Title + Price */
    var mtTop = createElement('div', {});
    mtTop.style.cssText = 'padding:24px 24px 0;display:flex;flex-direction:column;gap:8px;margin-bottom:12px;';

    var titleEl = createElement('span', {});
    titleEl.textContent = prod.title || 'Heat Press Machine';
    titleEl.style.cssText = 'font-size:24px;font-weight:800;color:#fff;line-height:1.25;';
    mtTop.appendChild(titleEl);

    var priceEl = createElement('span', {});
    priceEl.textContent = '$' + formatPrice((prod && prod.price) ? prod.price : 0);
    priceEl.style.cssText = 'font-size:32px;font-weight:800;color:#fff;';
    mtTop.appendChild(priceEl);
    modal.appendChild(mtTop);

    var specsContainer = createElement('div', {});
    specsContainer.style.cssText = 'padding:0 24px 24px;';

    var specItems = [
      ['Capacity', prod.capacity],
      ['Voltage', prod.voltage],
      ['Watts', prod.watts],
      ['Platen Size', (Math.round(prod.platenW) || '') + ' x ' + (Math.round(prod.platenH) || 0) + '"'],
      ['Weight', prod.weight],
      ['Temp Range', prod.tempRange],
      ['Method', prod.method]
    ];

    for (var i = 0; i < specItems.length; i++) {
      if (!specItems[i][1]) continue;
      var row = createElement('div', {});
      row.style.cssText = 'display:flex;justify-content:space-between;align-items:center;padding:8px 0;border-bottom:1px solid #444;';
      var lbl = createElement('span', {});
      lbl.textContent = specItems[i][0];
      lbl.style.cssText = 'font-size:12px;color:#fff;padding:4px 8px;';
      var val = createElement('span', {});
      val.textContent = specItems[i][1];
      val.style.cssText = 'font-size:13px;color:#fff;font-weight:600;';
      row.appendChild(lbl);
      row.appendChild(val);
      specsContainer.appendChild(row);
    }

    /* CTA buttons */
    var ctaWrap = createElement('div', {});
    ctaWrap.style.cssText = 'padding:24px 24px 32px;display:flex;gap:12px;align-items:center;justify-content:center;flex-wrap:wrap;';
    
    var addBtn = createElement('button', {});
    addBtn.textContent = 'Add to Cart';
    addBtn.style.cssText = 'padding:14px 28px;background:#3780C3;color:#fff;border:none;border-radius:12px;font-size:15px;font-weight:700;cursor:pointer;';
    addBtn.addEventListener('click', function() { 
      addToCart(prod); 
      overlay.remove(); 
    });
    ctaWrap.appendChild(addBtn);

    appendChildren(modal, [specsContainer, ctaWrap]);
    overlay.appendChild(modal);

    ((parent) => {
      parent.appendChild(overlay);
    })(document.body);

    overlay.addEventListener('click', function(e) {
      if (e.target === overlay) overlay.remove();
    });
  }

/* =========================================================================
   KEYBOARD NAV — arrow key support for option card grid.
    ========================================================================= */
  function focusCard(container, dir) {
    var cards = Array.from(container.querySelectorAll('.pc-opt-card'));
    if (!cards.length) return;
    var cur = container.querySelector('.pc-opt-card:focus');
    var idx = -1;
    for (var i = 0; i < cards.length; i++) {
      if (cards[i] === cur) { idx = i; break; }
    }
    idx += dir;
    if (idx < 0) idx = cards.length - 1;
    if (idx >= cards.length) idx = 0;
    cards[idx].focus();
  }

/* =========================================================================
   INIT / BOOTSTRAP — called from Liquid DOMContentLoaded event.
    ========================================================================= */
  document.addEventListener('DOMContentLoaded', function() {
    var root = getRoot();
    if (root) {
      root.setAttribute('data-pc-initialized', 'true');
      window.PC_INIT();
    }
  });


    /* =========================================================================
     CONFETTI — celebratory launch on results reveal.
        ========================================================================= */
    function launchConfetti() {
      var colors = ["#3780C3", "#3FA9F5", "#f97316", "#fb923c", "#eab308"];

      for (var i = 0; i < 70; i++) {
        (function(idx) {
          setTimeout(function() {
            var confetto = document.createElement("div");
            confetto.className = "pc-confetti";
            confetto.style.position = "fixed";
            confetto.style.zIndex = "999999";
            confetto.style.pointerEvents = "none";
            confetto.style.left = Math.random() * 100 + "vw";
            confetto.style.top = "-10px";
            confetto.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
            var initOpacity = Math.random() * 0.5 + 0.5;
            confetto.style.opacity = String(initOpacity);
            confetto.style.borderRadius = Math.random() > 0.5 ? "2px" : "50%";
            confetto.style.width = (Math.random() * 8 + 6) + "px";
            confetto.style.height = (Math.random() * 8 + 6) + "px";

            document.body.appendChild(confetto);

            var duration = Math.random() * 2600 + 2100;
            confetto.animate([
              { transform: "translateY(0) rotate(0deg) scale(1)", opacity: initOpacity },
              { transform: "translateY(" + (window.innerHeight + 120) + "px) rotate(" + (Math.random() * 280 + 80) + "deg) scale(0.5)", opacity: 0 }
            ], {
              duration: duration,
              easing: "cubic-bezier(0.22, 1, 0.36, 1)"
            }).onfinish = function() { confetto.remove(); };
          }, idx * 0.6);
        })(i);
      }
    }

})();
